import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import {
  IconHome, IconLayoutList, IconUser, IconLogout, IconMenu2,
  IconMail, IconCalendarEvent, IconBell, IconSettings, IconClock,
  IconFileText, IconShield, IconTool, IconChevronDown, IconStar,
  IconGolf, IconChevronLeft, IconChevronRight, IconX, IconFlag,
  IconSun, IconCloud, IconCloudRain, IconSnowflake, IconWind, IconCloudStorm,
  IconClipboardList, IconReportMoney, IconHelp, IconEdit, IconPlus, IconTrash,
  IconTrophy, IconThumbUp, IconThumbDown, IconCrown, IconDeviceFloppy,
  IconArrowsMinimize, IconArrowsMaximize, IconChartBar, IconDatabase, IconWallet,
  IconMapPin, IconPhoto,
} from '@tabler/icons-react';

import { useRef } from 'react';
import html2canvas from 'html2canvas';
import api from '../api/api';
import MyPage from './MyPage';
import { loadSettings, applySettings } from '../components/ThemeSettings';
import BoardListPage from './BoardListPage';
import BoardViewPage from './BoardViewPage';
import GolfReservationsPage from './GolfReservationsPage';
import ScoreCardPage from './ScoreCardPage';
import SchedulePage from './SchedulePage';
import MessagePage from './MessagePage';
import SignboardPage from './SignboardPage';
import SettlementPage from './SettlementPage';
import MarketAnalysisPage from './MarketAnalysisPage';
import KrxDataPage from './KrxDataPage';
import OutdoorActivityPage from './OutdoorActivityPage';
import StockLedgerPage from './StockLedgerPage';
import ChatWidget from '../components/ChatWidget';
import { ScratchSettingsLayer, calculateScratch, ScratchResultTable } from './ScratchBetting';
import BookingFormModal from '../components/BookingFormModal';
import { onBackdropMouseDown, onBackdropClick } from '../utils/modalDrag';

export default function HomePage({ setIsLoggedIn }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [me, setMe] = useState(null);
  const [boards, setBoards] = useState([]);
  const [navOpen, setNavOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [toast, setToast] = useState(null);
  const [siteConfig, setSiteConfig] = useState({ 'site.name': 'AI65 Neo', 'site.description': '' });
  const [userMenus, setUserMenus] = useState([]);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);
  const [unreadCount, setUnreadCount] = useState(0);

  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try { const res = await api.get('/messages/unread-count'); setUnreadCount(res.data.count); } catch {}
  }, []);

  useEffect(() => {
    applySettings(loadSettings());
    api.get('/auth/me').then(res => setMe(res.data)).catch(() => {});
    api.get('/boards').then(res => setBoards(res.data)).catch(() => {});
    api.get('/menus-front').then(res => setUserMenus(res.data || [])).catch(() => {});
    api.get('/notifications/active', { params: { target: 'USER' } }).then(res => setNotifications(res.data)).catch(() => {});
    fetchUnreadCount();
    api.get('/system-config').then(res => {
      setSiteConfig(prev => ({ ...prev, ...res.data }));
      if (res.data['site.favicon']) { let l = document.querySelector("link[rel~='icon']"); if (!l) { l = document.createElement('link'); l.rel = 'icon'; document.head.appendChild(l); } l.href = res.data['site.favicon']; }
      if (res.data['site.name']) document.title = res.data['site.name'];
    }).catch(() => {});
  }, []);

  // 30초마다 안읽은 쪽지 갱신
  useEffect(() => {
    const timer = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(timer);
  }, [fetchUnreadCount]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tokenStorage');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('refreshToken');
    setIsLoggedIn(false);
    navigate('/login');
  };

  // 프로필 드롭다운 외부 클릭 닫기
  useEffect(() => {
    const handler = (e) => { if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const Avatar = ({ size = 36, fontSize = 15 }) => (
    me?.profileImage ? (
      <img src={me.profileImage} alt="profile" style={{
        width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0,
      }} />
    ) : (
      <span style={{
        background: 'var(--tblr-primary, #206bc4)', color: '#fff',
        borderRadius: '50%', width: size, height: size,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize, fontWeight: 700, flexShrink: 0,
      }}>
        {me?.name?.[0]?.toUpperCase() || 'A'}
      </span>
    )
  );

  /* ── 고정 메뉴 ── */
  const STATIC_MENUS = [
    { key: 'schedule',  label: '일정', icon: IconCalendarEvent, path: '/schedule' },
    { key: 'signboard', label: '간판', icon: IconClipboardList,  path: '/signboard' },
    { key: 'settlement',label: '결산', icon: IconReportMoney,    path: '/settlement' },
    { key: 'messages',  label: '쪽지', icon: IconMail,           path: '/messages' },
    { key: 'stock-ledger', label: '주식장부', icon: IconWallet, path: '/stock-ledger' },
    { key: 'market-analysis', label: '시장분석', icon: IconChartBar, path: '/market-analysis' },
    { key: 'krx-data', label: 'KRX 데이터', icon: IconDatabase, path: '/krx-data' },
    { key: 'outdoor-activity', label: '야외활동', icon: IconMapPin, path: '/outdoor-activity' },
  ];

  if (!me) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <div className="spinner-border text-primary" />
      </div>
    );
  }

  return (
    <div className="page">
      {/* 토스트 */}
      {toast && (
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 9999 }}>
          <div className={`alert alert-${toast.type} alert-dismissible`} style={{ minWidth: 280, borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}>
            {toast.msg}
          </div>
        </div>
      )}

      {/* 상단 네비게이션 */}
      <header className="navbar navbar-expand-md" style={{ background: 'var(--tblr-bg-surface, #fff)', borderBottom: '1px solid var(--tblr-border-color, #e6e7e9)' }}>
        <div className="container-xl">
          <button className="navbar-toggler d-md-none" onClick={() => setNavOpen(v => !v)}>
            <IconMenu2 size={20} />
          </button>

          <a href="/" className="navbar-brand d-flex align-items-center gap-2" onClick={e => { e.preventDefault(); navigate('/'); }}>
            {siteConfig['site.logo'] ? (
              <img src={siteConfig['site.logo']} alt="로고" style={{ width: 32, height: 32, borderRadius: 8, objectFit: 'contain' }} />
            ) : (
              <div style={{
                width: 32, height: 32, borderRadius: 8,
                background: 'linear-gradient(135deg, #206bc4, #4263eb)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontWeight: 800, fontSize: 14,
              }}>{siteConfig['site.name']?.[0] || 'A'}</div>
            )}
            <span style={{ fontWeight: 700, fontSize: 18 }}>{siteConfig['site.name']}</span>
          </a>

          {/* 우측: 알림 + 프로필 드롭다운 */}
          <div className="navbar-nav flex-row order-md-last" style={{ position: 'relative' }}>
            {/* 알림 벨 */}
            <div className="nav-item me-2">
              <a className="nav-link px-2 position-relative" href="#" aria-label="쪽지"
                onClick={e => { e.preventDefault(); navigate('/messages'); }}>
                <IconBell size={20} />
                {unreadCount > 0 && (
                  <span style={{
                    position: 'absolute', top: 2, right: 2,
                    background: '#d63939', color: '#fff',
                    borderRadius: '50%', width: 17, height: 17,
                    fontSize: 10, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    lineHeight: 1, border: '2px solid var(--tblr-bg-surface, #fff)',
                  }}>
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </a>
            </div>
            <div className="nav-item dropdown" ref={profileRef}>
              <a className="nav-link d-flex lh-1 text-reset p-0" href="#"
                onClick={e => { e.preventDefault(); setProfileOpen(v => !v); }}
                style={{ userSelect: 'none', gap: 8 }}>
                <Avatar size={36} fontSize={15} />
                <div className="d-none d-xl-block">
                  <div style={{ fontWeight: 600, lineHeight: 1.3 }}>
                    {me.name}
                    {me.nickname && <span style={{ fontWeight: 400, opacity: 0.65, marginLeft: 5, fontSize: '0.85em' }}>({me.nickname})</span>}
                  </div>
                  <div className="mt-1" style={{ fontSize: '0.7rem', opacity: 0.6 }}>{me.email || ''}</div>
                </div>
                <IconChevronDown size={16} className="ms-1 align-self-center" style={{ opacity: 0.6 }} />
              </a>

              {profileOpen && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 10px)', right: 0,
                  width: 220, background: 'var(--tblr-bg-surface, #fff)',
                  border: '1px solid var(--tblr-border-color, #e6e7e9)',
                  borderRadius: 8, boxShadow: '0 4px 24px rgba(0,0,0,0.12)', zIndex: 2000,
                }}>
                  <div className="p-3 border-bottom">
                    <div className="d-flex align-items-center gap-2">
                      <Avatar size={40} fontSize={16} />
                      <div className="overflow-hidden">
                        <div className="fw-semibold text-truncate">
                          {me.name}
                          {me.nickname && <span className="fw-normal text-muted ms-1" style={{ fontSize: '0.85em' }}>({me.nickname})</span>}
                        </div>
                        <div className="text-muted small text-truncate">{me.email || ''}</div>
                      </div>
                    </div>
                    <div className="mt-2">
                      <span className="badge bg-primary-lt"><IconStar size={12} className="me-1" />{me.role || 'USER'}</span>
                    </div>
                  </div>
                  <div className="py-1">
                    <a href="#" className="dropdown-item d-flex align-items-center gap-2 py-2 px-3"
                      onClick={e => { e.preventDefault(); setProfileOpen(false); navigate('/mypage'); }}>
                      <IconUser size={18} className="text-muted" /> 프로필
                    </a>
                    <a href="#" className="dropdown-item d-flex align-items-center gap-2 py-2 px-3"
                      onClick={e => { e.preventDefault(); setProfileOpen(false); navigate('/mypage?tab=layout'); }}>
                      <IconSettings size={18} className="text-muted" /> 레이아웃
                    </a>
                    <a href="#" className="dropdown-item d-flex align-items-center gap-2 py-2 px-3"
                      onClick={e => { e.preventDefault(); setProfileOpen(false); navigate('/mypage'); }}>
                      <IconHelp size={18} className="text-muted" /> 도움말
                    </a>
                  </div>
                  <div className="border-top py-1">
                    <a href="#" className="dropdown-item d-flex align-items-center gap-2 py-2 px-3 text-danger"
                      onClick={e => { e.preventDefault(); handleLogout(); }}>
                      <IconLogout size={18} /> 로그아웃
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 좌측 메뉴 */}
          <div className={`collapse navbar-collapse ${navOpen ? 'show' : ''}`}>
            <ul className="navbar-nav">
              {STATIC_MENUS.map(m => (
                <li key={m.key} className="nav-item">
                  <a href={m.path}
                    className={`nav-link ${isActive(m.path) ? 'active' : ''}`}
                    onClick={e => { e.preventDefault(); navigate(m.path); setNavOpen(false); }}>
                    <m.icon size={16} className="me-1" /> {m.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </header>

      {/* 콘텐츠 */}
      <div className="page-wrapper">
        <div className="page-body">
          <div className="container-xl py-2">
            <Routes>
              <Route path="/" element={<><DashboardHome me={me} boards={boards} notifications={notifications} navigate={navigate} siteConfig={siteConfig} showToast={showToast} /><ChatWidget /></>} />
              <Route path="/board/:boardId" element={<BoardListPage me={me} showToast={showToast} />} />
              <Route path="/board/:boardId/post/:postId" element={<BoardViewPage me={me} showToast={showToast} />} />
              <Route path="/mypage" element={<MyPage me={me} setMe={setMe} showToast={showToast} />} />
              <Route path="/golf" element={<Navigate to="/" replace />} />
              <Route path="/golf/reservations" element={<GolfReservationsPage me={me} showToast={showToast} />} />
              <Route path="/golf/reservations/:id/scorecard" element={<ScoreCardPage showToast={showToast} />} />
              <Route path="/schedule" element={<SchedulePage me={me} showToast={showToast} />} />
              <Route path="/signboard" element={<SignboardPage me={me} showToast={showToast} />} />
              <Route path="/settlement" element={<SettlementPage me={me} showToast={showToast} />} />
              <Route path="/messages" element={<MessagePage me={me} showToast={showToast} onUnreadChange={fetchUnreadCount} />} />
              <Route path="/stock-ledger" element={<StockLedgerPage me={me} showToast={showToast} />} />
              <Route path="/market-analysis" element={<MarketAnalysisPage me={me} showToast={showToast} />} />
              <Route path="/krx-data" element={<KrxDataPage me={me} showToast={showToast} />} />
              <Route path="/outdoor-activity" element={<OutdoorActivityPage me={me} showToast={showToast} />} />
            </Routes>
          </div>
        </div>

        {/* 푸터 */}
        <footer className="footer footer-transparent d-print-none">
          <div className="container-xl">
            <div className="text-center text-muted py-3" style={{ fontSize: 13 }}>
              &copy; {new Date().getFullYear()} {siteConfig['site.name']}. All rights reserved.
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

/** 날씨 아이콘 */
const WEATHER_ICONS = {
  sunny:  { Icon: IconSun,        color: '#f59e0b' },
  cloudy: { Icon: IconCloud,      color: '#6b7280' },
  rain:   { Icon: IconCloudRain,  color: '#3b82f6' },
  snow:   { Icon: IconSnowflake,  color: '#60a5fa' },
  wind:   { Icon: IconWind,       color: '#8b5cf6' },
  storm:  { Icon: IconCloudStorm, color: '#ef4444' },
};

const WeatherBadge = ({ code, temperature }) => {
  const w = WEATHER_ICONS[code];
  if (!w) return null;
  return (
    <span className="d-inline-flex align-items-center gap-1">
      <w.Icon size={13} color={w.color} />
      {temperature && <span style={{ fontSize: 11, opacity: 0.7 }}>{temperature}</span>}
    </span>
  );
};

const DAY_NAMES = ['일', '월', '화', '수', '목', '금', '토'];
const formatDateWithDay = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  const day = DAY_NAMES[d.getDay()];
  const dayColor = d.getDay() === 0 ? '#e03131' : d.getDay() === 6 ? '#206bc4' : undefined;
  return { text: dateStr, day, dayColor };
};

/** 멀티셀렉트 드롭다운 */
function MultiSelect({ options, selected, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (val) => {
    onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
  };

  const selectedLabels = options.filter(o => selected.includes(o.value)).map(o => o.label);

  return (
    <div ref={ref} style={{ position: 'relative', minWidth: 160 }}>
      <div className="form-select form-select-sm d-flex align-items-center gap-1 flex-wrap"
        style={{ cursor: 'pointer', minHeight: 31, height: 'auto', paddingTop: 2, paddingBottom: 2 }}
        onClick={() => setOpen(v => !v)}>
        {selectedLabels.length === 0
          ? <span className="text-muted">{placeholder}</span>
          : selectedLabels.map((l, i) => (
            <span key={i} className="badge bg-blue-lt d-inline-flex align-items-center gap-1" style={{ fontSize: 11 }}>
              {l}
              <IconX size={10} style={{ cursor: 'pointer' }}
                onClick={e => { e.stopPropagation(); toggle(options.find(o => o.label === l)?.value); }} />
            </span>
          ))
        }
      </div>
      {open && (
        <div className="dropdown-menu show" style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1050, maxHeight: 220, overflowY: 'auto' }}>
          {options.map(o => (
            <label key={o.value} className="dropdown-item d-flex align-items-center gap-2" style={{ cursor: 'pointer', fontSize: 13 }}>
              <input type="checkbox" checked={selected.includes(o.value)} onChange={() => toggle(o.value)} />
              {o.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── 추이 미니 라인차트 (여러 시리즈, 데이터 없는 라운드는 선을 끊는다) ── */
function TrendMiniChart({ series, lines, fmtValue, xLabel }) {
  const getXLabel = xLabel || ((p) => String(p.date).slice(5));
  const [activeIdx, setActiveIdx] = useState(null);
  if (!series || series.length === 0) {
    return <div className="text-muted" style={{ fontSize: 11, padding: '6px 0' }}>추이 데이터가 없습니다.</div>;
  }
  const isMobile = window.innerWidth < 768;
  const W = isMobile ? 280 : 560;
  const H = 100;
  const PAD_L = 40, PAD_R = 10, PAD_T = 10, PAD_B = 18;
  const innerW = W - PAD_L - PAD_R, innerH = H - PAD_T - PAD_B;

  const allVals = series.flatMap(p => lines.map(l => p[l.key]).filter(v => v != null).map(Number));
  if (allVals.length === 0) {
    return <div className="text-muted" style={{ fontSize: 11, padding: '6px 0' }}>추이 데이터가 없습니다.</div>;
  }
  const rawMin = Math.min(...allVals), rawMax = Math.max(...allVals);
  const range = rawMax - rawMin || 1;
  const yFor = (v) => PAD_T + ((rawMax - v) / range) * innerH;
  const xFor = (i) => PAD_L + (series.length <= 1 ? 0 : (i / (series.length - 1)) * innerW);
  const labelStep = Math.max(1, Math.ceil(series.length / 6));
  const hitW = innerW / Math.max(series.length - 1, 1);

  const activePoint = activeIdx != null ? series[activeIdx] : null;
  const activeValues = activePoint ? lines.filter(l => activePoint[l.key] != null) : [];
  const tipW = Math.max(30, activeValues.length * 30);
  const tipCx = activePoint ? Math.min(Math.max(xFor(activeIdx), tipW / 2 + 1), W - tipW / 2 - 1) : 0;

  return (
    <svg width={W} height={H}>
      <text x={PAD_L - 6} y={yFor(rawMax) + 4} textAnchor="end" fontSize={9} fill="#7c8db0">{fmtValue(rawMax)}</text>
      <text x={PAD_L - 6} y={yFor(rawMin) + 4} textAnchor="end" fontSize={9} fill="#7c8db0">{fmtValue(rawMin)}</text>
      {lines.map(l => {
        const segments = [];
        let current = [];
        series.forEach((p, i) => {
          if (p[l.key] == null) {
            if (current.length > 1) segments.push(current);
            current = [];
          } else {
            current.push(`${xFor(i).toFixed(1)},${yFor(Number(p[l.key])).toFixed(1)}`);
          }
        });
        if (current.length > 1) segments.push(current);
        return segments.map((seg, si) => (
          <polyline key={`${l.key}-${si}`} points={seg.join(' ')} fill="none" stroke={l.color} strokeWidth={1.6} />
        ));
      })}
      {series.map((p, i) => (
        (i % labelStep === 0 || i === series.length - 1) && (
          <text key={`x-${i}`} x={xFor(i)} y={H - PAD_B + 14} textAnchor="middle" fontSize={8} fill="#7c8db0">
            {getXLabel(p, i)}
          </text>
        )
      ))}
      {activePoint && (
        <>
          <line x1={xFor(activeIdx)} y1={PAD_T} x2={xFor(activeIdx)} y2={H - PAD_B} stroke="#adb5bd" strokeDasharray="2,2" />
          {activeValues.map(l => (
            <circle key={`pt-${l.key}`} cx={xFor(activeIdx)} cy={yFor(Number(activePoint[l.key]))} r={2.8} fill={l.color} stroke="#fff" strokeWidth={1} />
          ))}
          <rect x={tipCx - tipW / 2} y={1} width={tipW} height={13} rx={3} fill="#ffffff" stroke="#ced4da" strokeWidth={0.5} />
          <text x={tipCx} y={10} textAnchor="middle" fontSize={9} fontWeight={600}>
            {activeValues.map((l, i) => (
              <tspan key={l.key} fill={l.color}>{i > 0 ? '  ' : ''}{fmtValue(activePoint[l.key])}</tspan>
            ))}
          </text>
        </>
      )}
      {series.map((p, i) => (
        <rect
          key={`hit-${i}`}
          x={xFor(i) - hitW / 2}
          y={0}
          width={hitW}
          height={H}
          fill="transparent"
          style={{ cursor: 'pointer' }}
          onClick={() => setActiveIdx(prev => prev === i ? null : i)}
        />
      ))}
    </svg>
  );
}

/* ── 개인별 핸디 추세보기 팝업 ── */
function GolfTrendModal({ member, initialYear, initialGameType, initialCount, color, onClose }) {
  const [year, setYear] = useState(initialYear || '');
  const [gameType, setGameType] = useState(initialGameType || '');
  const [count, setCount] = useState(initialCount || '');
  const [loading, setLoading] = useState(false);
  const [rounds, setRounds] = useState([]); // [{ reservationId, date, score, handicap, birdie, par, doublePar }]

  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let y = currentYear; y >= 2010; y--) yearOptions.push(y);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sortField: 'reservedDate', sortDir: 'asc', includeScores: true, size: 9999 };
      if (year) { params.dateFrom = `${year}-01-01`; params.dateTo = `${year}-12-31`; }
      if (gameType) params.gameType = gameType;
      const res = await api.get('/golf/reservations', { params });
      const data = res.data.content || [];
      const list = [];
      data.forEach(r => {
        if (r.status === 'CANCELLED' || r.useYn !== 'Y') return;
        const s = (r.scores || []).find(sc => sc.userId === member.id && sc.joinYn !== 'N' && sc.totalScore > 0);
        if (!s) return;
        let birdie = 0, par = 0, doublePar = 0;
        if (s.holeScoresJson) {
          try {
            const json = typeof s.holeScoresJson === 'string' ? JSON.parse(s.holeScoresJson) : s.holeScoresJson;
            (r.sections || []).forEach((sec, secIdx) => {
              const pars = parseParArray(sec.parData);
              const holes = json[`no${secIdx + 1}`]; if (!holes) return;
              Object.entries(holes).forEach(([holeNo, val]) => {
                const v = Number(val) || 0;
                const holePar = pars[Number(holeNo) - 1] || 0;
                if (v === -1) birdie++;
                else if (v === 0) par++;
                else if (holePar > 0 && v === holePar) doublePar++;
              });
            });
          } catch {}
        }
        list.push({
          reservationId: r.id, date: r.reservedDate,
          score: s.totalScore, handicap: s.overPar != null ? s.overPar : null,
          birdie, par, doublePar,
        });
      });
      const countN = count ? Number(count) : 0;
      setRounds(countN > 0 ? list.slice(-countN) : list);
    } catch {
      setRounds([]);
    } finally {
      setLoading(false);
    }
  }, [year, gameType, count, member.id]);

  useEffect(() => { load(); }, [load]);

  const scoreSeries = rounds.map(r => ({ date: r.date, score: r.score }));
  const handicapSeries = rounds.map(r => ({ date: r.date, handicap: r.handicap }));
  const countSeries = rounds.map(r => ({ date: r.date, birdie: r.birdie, par: r.par, doublePar: r.doublePar }));

  return (
    <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.55)' }} onMouseDown={onBackdropMouseDown(onClose)}>
      <div className="modal-dialog modal-dialog-centered modal-lg" onMouseDown={onBackdropClick}>
        <div className="modal-content" style={{ borderRadius: 14, overflow: 'hidden', border: 'none', boxShadow: '0 12px 32px rgba(0,0,0,0.25)' }}>
          <div style={{
            position: 'relative', padding: '16px 44px 14px 16px',
            background: `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)`, color: '#fff',
          }}>
            <button
              className="btn btn-icon btn-sm"
              onClick={onClose}
              style={{
                position: 'absolute', top: 10, right: 10, zIndex: 2,
                background: 'rgba(255,255,255,0.25)', border: 'none', color: '#fff',
                width: 28, height: 28, borderRadius: '50%',
              }}
            >
              <IconX size={16} />
            </button>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{member.nickname || member.name} — 추세보기</div>
          </div>

          <div className="modal-body" style={{ padding: '14px 18px' }}>
            <div className="d-flex gap-2 align-items-center flex-wrap mb-3">
              <select className="form-select form-select-sm" style={{ width: 'auto', minWidth: 80 }}
                value={count} onChange={e => setCount(e.target.value)}>
                <option value="">전체</option>
                {[5, 10, 20, 30, 50, 100].map(n => <option key={n} value={n}>최근 {n}</option>)}
              </select>
              <select className="form-select form-select-sm" style={{ width: 'auto', minWidth: 65 }}
                value={year} onChange={e => setYear(e.target.value)}>
                <option value="">년도</option>
                {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <select className="form-select form-select-sm" style={{ width: 'auto', minWidth: 70 }}
                value={gameType} onChange={e => setGameType(e.target.value)}>
                <option value="">유형</option>
                <option value="일반">일반</option>
                <option value="메이저">메이저</option>
              </select>
            </div>

            {loading ? (
              <div className="text-muted text-center py-4">불러오는 중...</div>
            ) : rounds.length === 0 ? (
              <div className="text-muted text-center py-4">해당 조건의 라운드 기록이 없습니다.</div>
            ) : (
              <>
                <div className="mb-3">
                  <div className="text-muted mb-1" style={{ fontSize: 12, fontWeight: 600 }}>스코어 추이</div>
                  <TrendMiniChart series={scoreSeries} lines={[{ key: 'score', color: '#e03131' }]} fmtValue={v => `${v}타`} />
                </div>
                <div className="mb-3">
                  <div className="text-muted mb-1" style={{ fontSize: 12, fontWeight: 600 }}>핸디(오버파) 추이</div>
                  <TrendMiniChart series={handicapSeries} lines={[{ key: 'handicap', color: '#1971c2' }]} fmtValue={v => `${v > 0 ? '+' : ''}${v}`} />
                </div>
                <div>
                  <div className="text-muted mb-1 d-flex align-items-center gap-2" style={{ fontSize: 12, fontWeight: 600 }}>
                    버디/파/더블파 추이
                    <span style={{ fontWeight: 400, fontSize: 11 }}>
                      <span style={{ color: '#c41000' }}>■</span> 버디
                      <span style={{ color: '#078f00', marginLeft: 6 }}>■</span> 파
                      <span style={{ color: '#00078f', marginLeft: 6 }}>■</span> 더블파
                    </span>
                  </div>
                  <TrendMiniChart
                    series={countSeries}
                    lines={[{ key: 'birdie', color: '#c41000' }, { key: 'par', color: '#078f00' }, { key: 'doublePar', color: '#00078f' }]}
                    fmtValue={v => `${v}개`}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── 전체 멤버 추세 비교 팝업 ── */
function GolfAllTrendModal({ members, initialYear, initialGameType, initialCount, colorFor, onClose }) {
  const [year, setYear] = useState(initialYear || '');
  const [gameType, setGameType] = useState(initialGameType || '');
  const [count, setCount] = useState(initialCount || '');
  const [loading, setLoading] = useState(false);
  const [series, setSeries] = useState([]); // [{ date, [`score_${id}`], [`handicap_${id}`] }]

  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let y = currentYear; y >= 2010; y--) yearOptions.push(y);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sortField: 'reservedDate', sortDir: 'asc', includeScores: true, size: 9999 };
      if (year) { params.dateFrom = `${year}-01-01`; params.dateTo = `${year}-12-31`; }
      if (gameType) params.gameType = gameType;
      const res = await api.get('/golf/reservations', { params });
      const data = res.data.content || [];
      const countN = count ? Number(count) : 0;

      // 멤버별로 각자의 최근 N경기를 독립적으로 추출 (날짜 정렬 아님)
      const memberLists = {};
      members.forEach(m => {
        const list = [];
        data.forEach(r => {
          if (r.status === 'CANCELLED' || r.useYn !== 'Y') return;
          const s = (r.scores || []).find(sc => sc.userId === m.id && sc.joinYn !== 'N' && sc.totalScore > 0);
          if (s) list.push({ date: r.reservedDate, score: s.totalScore, handicap: s.overPar != null ? s.overPar : null });
        });
        memberLists[m.id] = countN > 0 ? list.slice(-countN) : list;
      });

      // 각자의 마지막 경기가 서로 같은 위치(가장 최근)에 오도록 뒤에서부터 정렬
      const maxLen = Math.max(0, ...members.map(m => memberLists[m.id].length));
      const rows = [];
      for (let j = 0; j < maxLen; j++) {
        const row = { idx: j + 1 };
        members.forEach(m => {
          const list = memberLists[m.id];
          const item = list[list.length - maxLen + j];
          if (item) {
            row[`score_${m.id}`] = item.score;
            row[`handicap_${m.id}`] = item.handicap;
          }
        });
        rows.push(row);
      }
      setSeries(rows);
    } catch {
      setSeries([]);
    } finally {
      setLoading(false);
    }
  }, [year, gameType, count, members]);

  useEffect(() => { load(); }, [load]);

  const scoreLines = members.map(m => ({ key: `score_${m.id}`, color: colorFor(m) }));
  const handicapLines = members.map(m => ({ key: `handicap_${m.id}`, color: colorFor(m) }));

  return (
    <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.55)' }} onMouseDown={onBackdropMouseDown(onClose)}>
      <div className="modal-dialog modal-dialog-centered modal-lg" onMouseDown={onBackdropClick}>
        <div className="modal-content" style={{ borderRadius: 14, overflow: 'hidden', border: 'none', boxShadow: '0 12px 32px rgba(0,0,0,0.25)' }}>
          <div style={{
            position: 'relative', padding: '16px 44px 14px 16px',
            background: 'linear-gradient(135deg, #495057 0%, #495057cc 100%)', color: '#fff',
          }}>
            <button
              className="btn btn-icon btn-sm"
              onClick={onClose}
              style={{
                position: 'absolute', top: 10, right: 10, zIndex: 2,
                background: 'rgba(255,255,255,0.25)', border: 'none', color: '#fff',
                width: 28, height: 28, borderRadius: '50%',
              }}
            >
              <IconX size={16} />
            </button>
            <div style={{ fontWeight: 700, fontSize: 16 }}>전체 멤버 추세 비교</div>
          </div>

          <div className="modal-body" style={{ padding: '14px 18px' }}>
            <div className="d-flex gap-2 align-items-center flex-wrap mb-2">
              <select className="form-select form-select-sm" style={{ width: 'auto', minWidth: 80 }}
                value={count} onChange={e => setCount(e.target.value)}>
                <option value="">전체</option>
                {[5, 10, 20, 30, 50, 100].map(n => <option key={n} value={n}>최근 {n}</option>)}
              </select>
              <select className="form-select form-select-sm" style={{ width: 'auto', minWidth: 65 }}
                value={year} onChange={e => setYear(e.target.value)}>
                <option value="">년도</option>
                {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <select className="form-select form-select-sm" style={{ width: 'auto', minWidth: 70 }}
                value={gameType} onChange={e => setGameType(e.target.value)}>
                <option value="">유형</option>
                <option value="일반">일반</option>
                <option value="메이저">메이저</option>
              </select>
            </div>

            <div className="d-flex flex-wrap gap-2 mb-3" style={{ fontSize: 11 }}>
              {members.map(m => (
                <span key={m.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: colorFor(m), display: 'inline-block' }} />
                  {m.nickname || m.name}
                </span>
              ))}
            </div>

            {loading ? (
              <div className="text-muted text-center py-4">불러오는 중...</div>
            ) : series.length === 0 ? (
              <div className="text-muted text-center py-4">해당 조건의 라운드 기록이 없습니다.</div>
            ) : (
              <>
                <div className="mb-3">
                  <div className="text-muted mb-1" style={{ fontSize: 12, fontWeight: 600 }}>스코어 추이 (각자 최근 경기 기준, 오른쪽이 최신)</div>
                  <TrendMiniChart series={series} lines={scoreLines} fmtValue={v => `${v}타`} xLabel={(p) => (p.idx === series.length ? '최근' : `-${series.length - p.idx}`)} />
                </div>
                <div>
                  <div className="text-muted mb-1" style={{ fontSize: 12, fontWeight: 600 }}>핸디(오버파) 추이 (각자 최근 경기 기준, 오른쪽이 최신)</div>
                  <TrendMiniChart series={series} lines={handicapLines} fmtValue={v => `${v > 0 ? '+' : ''}${v}`} xLabel={(p) => (p.idx === series.length ? '최근' : `-${series.length - p.idx}`)} />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const STATUS_MAP = {
  PENDING:   '대기',
  CANCELLED: '취소',
  COMPLETED: '완료',
};

const parseParArray = (parData) => {
  if (!parData) return [];
  try { return JSON.parse(parData); } catch {
    return parData.split(',').map(v => Number(v.trim())).filter(v => !isNaN(v));
  }
};

const HoleBadge = ({ sections }) => {
  if (!sections || sections.length === 0) return null;
  const h0 = sections[0]?.holesCount || 9;
  if (sections.length === 1) return <span className="badge bg-azure-lt" style={{ fontSize: 10 }}>{h0}홀</span>;
  const h1 = sections[1]?.holesCount || 9;
  if (sections[0]?.sectionId === sections[1]?.sectionId)
    return <span className="badge bg-purple-lt" style={{ fontSize: 10 }}>{h0}x2</span>;
  return <span className="badge bg-green-lt" style={{ fontSize: 10 }}>{h0 + h1}홀</span>;
};

/** 이미지 파일을 지정된 최대 변 길이(px)에 맞춰 비율 유지하며 JPEG로 리사이즈 */
function resizeImageFile(file, maxSize, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      let { width, height } = img;
      if (width > maxSize || height > maxSize) {
        const scale = Math.min(maxSize / width, maxSize / height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob(blob => {
        URL.revokeObjectURL(url);
        if (blob) resolve(blob); else reject(new Error('이미지 변환 실패'));
      }, 'image/jpeg', quality);
    };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

/** 홈 대시보드 */
function DashboardHome({ me, boards, notifications, navigate, siteConfig, showToast }) {
  const isDark = document.documentElement.getAttribute('data-bs-theme') === 'dark';
  const mc = (m) => (isDark ? m.darkColor : m.color) || m.color || undefined;

  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalElements, setTotalElements] = useState(0);
  const [members, setMembers] = useState([]);
  const [courses, setCourses] = useState([]);

  // 필터 (sessionStorage로 유지)
  const [selectedCourseIds, setSelectedCourseIds] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('home_courseIds') || '[]'); } catch { return []; }
  });
  const [gameCount, setGameCount] = useState(() => sessionStorage.getItem('home_gameCount') ?? '5');
  const [year, setYear] = useState(() => sessionStorage.getItem('home_year') ?? '');
  const [filterGameType, setFilterGameType] = useState(() => sessionStorage.getItem('home_gameType') ?? '');
  const [showLegend, setShowLegend] = useState(false);
  const [compact, setCompact] = useState(() => sessionStorage.getItem('home_compact') === 'true');

  useEffect(() => { sessionStorage.setItem('home_courseIds', JSON.stringify(selectedCourseIds)); }, [selectedCourseIds]);
  useEffect(() => { sessionStorage.setItem('home_gameCount', gameCount); }, [gameCount]);
  useEffect(() => { sessionStorage.setItem('home_year', year); }, [year]);
  useEffect(() => { sessionStorage.setItem('home_gameType', filterGameType); }, [filterGameType]);
  useEffect(() => { sessionStorage.setItem('home_compact', String(compact)); }, [compact]);

  // 팝업 알림
  const [notiPopup, setNotiPopup] = useState(null);
  const [notiDismissed, setNotiDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem('dismissedNotiPopups') || '{}'); } catch { return {}; }
  });
  // 배너 알림 닫기 (세션 내) + 배너 본문 팝업
  const [closedBanners, setClosedBanners] = useState(new Set());
  const [bannerDetail, setBannerDetail] = useState(null);
  // 행 필터 토글 (복수 선택 가능)
  const [rowFilters, setRowFilters] = useState(new Set());
  const toggleRowFilter = (key) => setRowFilters(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  // 통계용 전체 데이터
  const [allReservations, setAllReservations] = useState([]);
  // 개인별 통계 근거 라운드 리스트 팝업
  const [statGamesModal, setStatGamesModal] = useState(null); // { member, games }
  // 개인별 핸디 추세보기 팝업
  const [trendModal, setTrendModal] = useState(null); // { member }
  // 전체 멤버 추세 비교 팝업
  const [allTrendModal, setAllTrendModal] = useState(false);

  // 달력
  const nowD = new Date();
  const [calYear, setCalYear] = useState(nowD.getFullYear());
  const [calMonth, setCalMonth] = useState(nowD.getMonth() + 1);
  const [calSchedules, setCalSchedules] = useState([]);
  const [calBookings, setCalBookings] = useState([]);
  const [calSelectedDay, setCalSelectedDay] = useState(null);
  const [bookingModal, setBookingModal] = useState(false);
  const [editBooking, setEditBooking] = useState(null);
  const [scheduleModal, setScheduleModal] = useState(false);
  const [editSchedule, setEditSchedule] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({ title: '', description: '', startDate: '', endDate: '' });
  const [scheduleSaving, setScheduleSaving] = useState(false);

  // 스코어카드 모달
  const [scoreReservation, setScoreReservation] = useState(null);
  const [scoreData, setScoreData] = useState([]);
  const [scoreLoading, setScoreLoading] = useState(false);

  const openScoreCardModal = async (r) => {
    setScoreReservation(r);
    setScoreLoading(true);
    try {
      const res = await api.get(`/golf/reservations/${r.id}/scores`);
      setScoreData(res.data || []);
    } catch {
      setScoreData([]);
    } finally {
      setScoreLoading(false);
    }
  };

  const openScoreCard = async (r) => {
    // 모바일: 페이지 이동
    if (window.innerWidth < 768) {
      navigate(`/golf/reservations/${r.id}/scorecard`, { state: { reservation: r } });
      return;
    }
    // PC: 모달
    openScoreCardModal(r);
  };

  // 개인별 통계 팝업에서 날짜 클릭 시: 모바일 여부와 관계없이 항상 레이어(모달)로 스코어카드 표시
  const openScoreCardFromStat = (reservationId) => {
    const r = allReservations.find(x => x.id === reservationId);
    if (!r) return;
    setStatGamesModal(null);
    openScoreCardModal(r);
  };

  const pendingRefresh = useRef(false);
  const handlePhotosChange = () => { pendingRefresh.current = true; };
  const handleSaveScores = async (updatedScores) => {
    if (!scoreReservation || !scoreReservation.id) return;
    try {
      const res = await api.put(`/golf/reservations/${scoreReservation.id}/scores`, updatedScores);
      setScoreData(res.data || []);
      showToast('저장되었습니다.');
      pendingRefresh.current = true; // 모달 닫을 때 새로고침
    } catch {
      showToast('저장에 실패했습니다.', 'danger');
    }
  };

  const handleDeleteScoreReservation = async () => {
    if (!scoreReservation?.id) return;
    try {
      await api.delete(`/golf/reservations/${scoreReservation.id}`);
      setReservations(prev => prev.filter(r => r.id !== scoreReservation.id));
      setAllReservations(prev => prev.filter(r => r.id !== scoreReservation.id));
      closeScoreCard();
      showToast('예약이 삭제되었습니다.');
    } catch {
      showToast('삭제에 실패했습니다.', 'danger');
    }
  };

  const [newCardOpen, setNewCardOpen] = useState(false);

  const openNewScoreCard = () => {
    const today = new Date().toLocaleDateString('sv');
    const tempReservation = {
      id: null,
      golfCourseId: '',
      golfCourseName: '',
      reservedDate: today,
      reservedTime: '12:00:00',
      playerCount: 4,
      status: 'PENDING',
      useYn: 'Y',
      gameType: '일반',
      greenFee: null,
      weather: '',
      temperature: '',
      totalHoles: '',
      sections: [],
    };
    // 모바일: 페이지 이동
    if (window.innerWidth < 768) {
      navigate('/golf/reservations/new/scorecard', { state: { reservation: tempReservation, isNew: true } });
      return;
    }
    // PC: 모달
    setScoreReservation(tempReservation);
    setScoreData([]);
    setScoreLoading(false);
    setNewCardOpen(true);
  };

  const closeScoreCard = () => {
    setScoreReservation(null);
    setScoreData([]);
    setNewCardOpen(false);
    if (pendingRefresh.current) {
      pendingRefresh.current = false;
      loadData();
    }
  };

  const fetchCalData = useCallback(async (y, m) => {
    try { const res = await api.get(`/schedules?year=${y}&month=${m}`); setCalSchedules(res.data); } catch { setCalSchedules([]); }
    try {
      const dateFrom = `${y}-${String(m).padStart(2,'0')}-01`;
      const dateTo = `${y}-${String(m).padStart(2,'0')}-${new Date(y, m, 0).getDate()}`;
      const res = await api.get('/golf/reservations', { params: { dateFrom, dateTo, size: 100, sortField: 'reservedTime', sortDir: 'asc', includeScores: false } });
      setCalBookings(res.data.content || []);
    } catch { setCalBookings([]); }
  }, []);

  const calPrev = () => { const nm = calMonth===1?12:calMonth-1; const ny = calMonth===1?calYear-1:calYear; setCalMonth(nm); setCalYear(ny); setCalSelectedDay(null); fetchCalData(ny,nm); };
  const calNext = () => { const nm = calMonth===12?1:calMonth+1; const ny = calMonth===12?calYear+1:calYear; setCalMonth(nm); setCalYear(ny); setCalSelectedDay(null); fetchCalData(ny,nm); };

  const getCalSchedules = (day) => { if(!day) return []; const ds=`${calYear}-${String(calMonth).padStart(2,'0')}-${String(day).padStart(2,'0')}`; return calSchedules.filter(s=>s.startDate<=ds&&s.endDate>=ds); };
  const getCalBookings = (day) => { if(!day) return []; const ds=`${calYear}-${String(calMonth).padStart(2,'0')}-${String(day).padStart(2,'0')}`; return calBookings.filter(b=>b.reservedDate===ds); };

  const handleScheduleSave = async () => {
    if (!scheduleForm.title.trim()) { showToast('제목을 입력해주세요.', 'danger'); return; }
    setScheduleSaving(true);
    try {
      if (editSchedule) { await api.put(`/schedules/${editSchedule.id}`, scheduleForm); showToast('일정이 수정되었습니다.'); }
      else { await api.post('/schedules', scheduleForm); showToast('일정이 등록되었습니다.'); }
      setScheduleModal(false); setEditSchedule(null); fetchCalData(calYear, calMonth);
    } catch { showToast('일정 저장에 실패했습니다.', 'danger'); }
    finally { setScheduleSaving(false); }
  };
  const handleScheduleDelete = async (id) => {
    if (!window.confirm('일정을 삭제하시겠습니까?')) return;
    try { await api.delete(`/schedules/${id}`); showToast('삭제되었습니다.'); fetchCalData(calYear, calMonth); } catch { showToast('삭제 실패', 'danger'); }
  };

  useEffect(() => {
    api.get('/golf/members').then(res => setMembers(res.data || [])).catch(() => {});
    api.get('/golf/courses').then(res => setCourses(res.data?.content || res.data || [])).catch(() => {});
    fetchCalData(calYear, calMonth);
    // 팝업 알림: 오늘 dismiss하지 않은 첫 팝업 표시
    const todayStr = new Date().toLocaleDateString('sv');
    const popup = (notifications || []).find(n => n.type === 'POPUP' && n.isActive && notiDismissed[n.id] !== todayStr);
    if (popup) setNotiPopup(popup);
  }, []);

  // 전체 데이터에서 멤버별 통계 + 경기수 포함/제외 판별
  const { memberStats, memberGameMap } = useMemo(() => {
    const gc = gameCount ? Number(gameCount) : 0;
    // 멤버별로 참여한 유효 경기 수집 (날짜 내림차순 = allReservations 순서 유지)
    const perUser = {}; // userId -> [{ reservationId, score, holeScoresJson }]
    allReservations.forEach(r => {
      if (!r.scores) return;
      if (r.status === 'CANCELLED') return; // 취소된 경기는 통계 제외
      r.scores.forEach(s => {
        if (!s.userId || s.joinYn === 'N' || !s.totalScore || s.totalScore <= 0) return;
        if (r.useYn !== 'Y') return; // 유효='Y'만
        if (!perUser[s.userId]) perUser[s.userId] = [];
        perUser[s.userId].push({
          reservationId: r.id, score: s.totalScore, holeScoresJson: s.holeScoresJson, sections: r.sections,
          reservedDate: r.reservedDate, reservedTime: r.reservedTime, golfCourseName: r.golfCourseName,
        });
      });
    });

    // 멤버별 경기수에 포함된 예약ID / 제외된 예약ID
    const gameMap = {}; // userId -> { included: Set, excluded: Set }

    const stats = Object.entries(perUser).map(([uid, games]) => {
      const userId = Number(uid);
      let targetGames = games; // 전체
      const included = new Set();
      const excluded = new Set();

      if (gc > 0) {
        // 최근 gc+2건 선택
        const picked = games.slice(0, gc + 2);
        picked.forEach(g => included.add(g.reservationId));

        if (picked.length > 2) {
          // 최저/최고 제외
          const sorted = [...picked].sort((a, b) => a.score - b.score);
          const bestIdx = sorted[0]; // 최저
          const worstIdx = sorted[sorted.length - 1]; // 최고
          excluded.add(bestIdx.reservationId);
          excluded.add(worstIdx.reservationId);
          targetGames = picked.filter(g => g.reservationId !== bestIdx.reservationId && g.reservationId !== worstIdx.reservationId);
        } else {
          targetGames = picked;
        }
      }

      gameMap[userId] = { included, excluded };

      // 통계 계산
      let eagle = 0, birdie = 0, par = 0, bogey = 0;
      const scores = targetGames.map(g => g.score);
      targetGames.forEach(g => {
        if (g.holeScoresJson) {
          try {
            const json = typeof g.holeScoresJson === 'string' ? JSON.parse(g.holeScoresJson) : g.holeScoresJson;
            (g.sections || []).forEach((sec, secIdx) => {
              const pars = parseParArray(sec.parData);
              const holes = json[`no${secIdx + 1}`]; if (!holes) return;
              Object.entries(holes).forEach(([holeNo, val]) => {
                const v = Number(val) || 0;
                const holePar = pars[Number(holeNo) - 1] || 0;
                // 양파: 해당 홀 파의 2배 스코어 (파3→+3, 파4→+4, 파5→+5 ...)
                if (v <= -2) eagle++; else if (v === -1) birdie++;
                else if (v === 0) par++; else if (holePar > 0 && v === holePar) bogey++;
              });
            });
          } catch {}
        }
      });

      // 제외 전 전체(N+2건) 통계
      const allGames = gc > 0 ? games.slice(0, gc + 2) : games;
      const allScores = allGames.map(g => g.score);
      // 근거 라운드 리스트 (클릭 시 팝업 노출용, 최신순 유지)
      const recentGames = allGames.map(g => ({ ...g, excluded: excluded.has(g.reservationId) }));

      return {
        userId, playCount: scores.length,
        avgScore: scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length * 10) / 10 : 0,
        bestScore: scores.length > 0 ? Math.min(...scores) : 0,
        worstScore: scores.length > 0 ? Math.max(...scores) : 0,
        eagle, birdie, par, bogey,
        // 제외 전 전체 정보
        allPlayCount: allScores.length,
        allAvgScore: allScores.length > 0 ? Math.round(allScores.reduce((a, b) => a + b, 0) / allScores.length * 10) / 10 : 0,
        allBest: allScores.length > 0 ? Math.min(...allScores) : 0,
        allWorst: allScores.length > 0 ? Math.max(...allScores) : 0,
        recentGames,
      };
    });

    return { memberStats: stats, memberGameMap: gameMap };
  }, [allReservations, gameCount]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        sortField: 'reservedDate', sortDir: 'desc', includeScores: true,
        size: 9999,
      };
      if (selectedCourseIds.length > 0) params.courseIds = selectedCourseIds.join(',');
      if (year) { params.dateFrom = `${year}-01-01`; params.dateTo = `${year}-12-31`; }
      if (filterGameType) params.gameType = filterGameType;

      const res = await api.get('/golf/reservations', { params });
      const data = res.data.content || [];
      setReservations(data);
      setAllReservations(data);
      setTotalElements(res.data.totalElements || 0);
    } catch {
      setReservations([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCourseIds, year, filterGameType]);

  useEffect(() => { loadData(); }, [loadData]);

  const updateFilter = (setter) => (val) => { setter(val); };

  const findScoreForMember = (reservation, memberId) => {
    if (!reservation.scores) return null;
    return reservation.scores.find(s => s.userId === memberId && s.joinYn !== 'N');
  };

  // 년도 옵션 생성 (현재 년도 ~ 2010)
  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let y = currentYear; y >= 2010; y--) yearOptions.push(y);


  return (
    <>
      {/* 배너 알림 (달력 위) */}
      {notifications.filter(n => n.type === 'BANNER' && n.isActive && !closedBanners.has(n.id)).map(n => (
        <div key={n.id} className="d-flex align-items-center mb-2" style={{
          background: n.bgColor || '#e7f3ff', color: n.textColor || '#0d6efd',
          padding: '8px 16px', borderRadius: 8, fontSize: 14,
        }}>
          <div style={{ flex: 1, textAlign: 'center', cursor: 'pointer' }}
            onClick={() => setBannerDetail(n)}>
            <span style={{ textDecoration: 'underline', fontWeight: 600 }}>{n.title}</span>
          </div>
          <button className="btn btn-sm p-0 ms-2" style={{ color: n.textColor || '#0d6efd', opacity: 0.6, lineHeight: 1 }}
            onClick={() => setClosedBanners(prev => new Set(prev).add(n.id))}>
            <IconX size={16} />
          </button>
        </div>
      ))}

      {/* 팝업 알림 모달 */}
      {notiPopup && (
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1060 }} />
          <div className="modal modal-blur fade show" style={{ display: 'block', zIndex: 1061 }}
            onMouseDown={onBackdropMouseDown} onClick={(e) => onBackdropClick(e, () => setNotiPopup(null))}>
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 500 }}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title d-flex align-items-center gap-2">
                    <IconBell size={20} className="text-warning" />
                    {notiPopup.title}
                  </h5>
                  <button type="button" className="btn-close" onClick={() => setNotiPopup(null)} />
                </div>
                {notiPopup.content && (
                  <div className="modal-body" dangerouslySetInnerHTML={{ __html: notiPopup.content }} />
                )}
                <div className="modal-footer justify-content-between">
                  <button className="btn btn-ghost-secondary btn-sm" onClick={() => {
                    const todayStr = new Date().toLocaleDateString('sv');
                    const updated = { ...notiDismissed, [notiPopup.id]: todayStr };
                    setNotiDismissed(updated);
                    localStorage.setItem('dismissedNotiPopups', JSON.stringify(updated));
                    setNotiPopup(null);
                  }}>오늘 하루 안 보기</button>
                  <div className="ms-auto d-flex gap-2">
                    {notiPopup.linkUrl && (
                      <a href={notiPopup.linkUrl} target="_blank" rel="noopener noreferrer"
                        className="btn btn-primary btn-sm">자세히 보기</a>
                    )}
                    <button className="btn btn-secondary btn-sm" onClick={() => setNotiPopup(null)}>닫기</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* 배너 본문 팝업 */}
      {bannerDetail && (
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1060 }} />
          <div className="modal modal-blur fade show" style={{ display: 'block', zIndex: 1061 }}
            onMouseDown={onBackdropMouseDown} onClick={(e) => onBackdropClick(e, () => setBannerDetail(null))}>
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 500 }}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title d-flex align-items-center gap-2">
                    <IconBell size={18} className="text-primary" />
                    {bannerDetail.title}
                  </h5>
                  <button type="button" className="btn-close" onClick={() => setBannerDetail(null)} />
                </div>
                {bannerDetail.content && (
                  <div className="modal-body" dangerouslySetInnerHTML={{ __html: bannerDetail.content }} />
                )}
                <div className="modal-footer">
                  {bannerDetail.linkUrl && (
                    <a href={bannerDetail.linkUrl} target="_blank" rel="noopener noreferrer"
                      className="btn btn-primary btn-sm">자세히 보기</a>
                  )}
                  <button className="btn btn-sm" onClick={() => setBannerDetail(null)}>닫기</button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── 달력 ── */}
      <div className="mb-4">
      {(() => {
        const CAL_DAYS = ['일','월','화','수','목','금','토'];
        const CAL_COLORS = ['#206bc4','#2fb344','#d63939','#f76707','#ae3ec9','#0ca678','#d6336c'];
        const firstDay = new Date(calYear, calMonth-1, 1).getDay();
        const daysInMonth = new Date(calYear, calMonth, 0).getDate();
        const cells = []; for(let i=0;i<firstDay;i++) cells.push(null); for(let d=1;d<=daysInMonth;d++) cells.push(d); while(cells.length%7!==0) cells.push(null);
        const rows = []; for(let i=0;i<cells.length;i+=7) rows.push(cells.slice(i,i+7));
        const todayDt = new Date(); const isTodayMonth = todayDt.getFullYear()===calYear && todayDt.getMonth()+1===calMonth; const todayDate = isTodayMonth ? todayDt.getDate() : null;
        return (
          <div className="card">
            <div className="card-header d-flex align-items-center py-1 px-2">
              <button className="btn btn-ghost-secondary btn-sm px-1" onClick={calPrev}><IconChevronLeft size={14} /></button>
              <h3 className="card-title mb-0 mx-1" style={{ minWidth:80, textAlign:'center', cursor:'pointer', fontSize: 14 }}
                onClick={() => navigate('/schedule')}>
                <span className="text-primary">{calYear}년 {calMonth}월</span>
              </h3>
              <button className="btn btn-ghost-secondary btn-sm px-1" onClick={calNext}><IconChevronRight size={14} /></button>
              <div className="ms-auto d-flex gap-1">
                <button className="btn btn-sm btn-success px-2 py-0" style={{ fontSize: 12 }} onClick={() => { setEditBooking(null); setBookingModal(true); }}>
                  <IconFlag size={12} className="me-1" />부킹
                </button>
                <button className="btn btn-sm btn-primary px-2 py-0" style={{ fontSize: 12 }} onClick={() => {
                  const todayStr = new Date().toISOString().slice(0,10);
                  setEditSchedule(null); setScheduleForm({ title:'', description:'', startDate:todayStr, endDate:todayStr }); setScheduleModal(true);
                }}>
                  <IconPlus size={12} className="me-1" />일정
                </button>
              </div>
            </div>
            <div className="card-body p-0">
              <table className="table table-bordered mb-0" style={{ tableLayout:'fixed' }}>
                <thead>
                  <tr>{CAL_DAYS.map((d,i) => <th key={d} className="text-center py-1 cal-day-header" style={{ fontSize:12, fontWeight:600, background:'var(--tblr-bg-surface-secondary, #f8f9fa)', color:i===0?'#d63939':i===6?'#206bc4':'inherit' }}>{d}</th>)}</tr>
                </thead>
                <tbody>
                  {rows.map((row,ri) => (
                    <tr key={ri}>
                      {row.map((day,ci) => {
                        const evts = getCalSchedules(day);
                        const bkgs = getCalBookings(day);
                        const allItems = [...evts.map(s=>({type:'schedule',data:s})), ...bkgs.map(b=>({type:'booking',data:b}))];
                        const isToday = day===todayDate; const isSel = day===calSelectedDay;
                        return (
                          <td key={ci} className={isSel?'cal-cell-selected':isToday?'cal-cell-today':''} style={{ height:68, verticalAlign:'top', padding:'2px 4px', cursor:day?'pointer':'default', background:!day?'var(--tblr-bg-surface-secondary, #f8f9fa)':isSel?'rgba(32,107,196,0.12)':isToday?'rgba(255,252,230,0.3)':'transparent' }}
                            onClick={() => day && setCalSelectedDay(day===calSelectedDay?null:day)}>
                            {day && (<>
                              <div style={{ fontSize:12, fontWeight:isToday?700:400, color:ci===0?'#d63939':ci===6?'#206bc4':'inherit' }}>
                                {isToday ? <span style={{ background:'#206bc4',color:'#fff',borderRadius:'50%',width:20,height:20,display:'inline-flex',alignItems:'center',justifyContent:'center',fontSize:11 }}>{day}</span> : day}
                              </div>
                              {allItems.slice(0,2).map((item,idx) => item.type==='schedule' ? (
                                <div key={`s-${item.data.id}`} style={{ fontSize:10,padding:'0 3px',marginBottom:1,borderRadius:2,color:'#fff',background:CAL_COLORS[item.data.id%CAL_COLORS.length],overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',cursor:'pointer' }}
                                  onClick={e => { e.stopPropagation(); setEditSchedule(item.data); setScheduleForm({ title:item.data.title, description:item.data.description||'', startDate:item.data.startDate, endDate:item.data.endDate }); setScheduleModal(true); }}>
                                  {item.data.title}
                                </div>
                              ) : (
                                <div key={`b-${item.data.id}`} style={{ fontSize:10,padding:'0 3px',marginBottom:1,borderRadius:2,display:'flex',alignItems:'center',gap:2,color:item.data.gameType==='메이저'?'#ae3ec9':'#2fb344',background:item.data.gameType==='메이저'?'#f3e8ff':'#e6f9ed',overflow:'hidden',whiteSpace:'nowrap',cursor:'pointer' }}
                                  onClick={e => { e.stopPropagation(); setEditBooking(item.data); setBookingModal(true); }}>
                                  <IconFlag size={8} style={{flexShrink:0}} />
                                  <span style={{overflow:'hidden',textOverflow:'ellipsis'}}>{item.data.reservedTime?.slice(0,5)} {item.data.golfCourseName}</span>
                                </div>
                              ))}
                              {allItems.length>2 && <div style={{fontSize:9,color:'#888'}}>+{allItems.length-2}</div>}
                            </>)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* 선택 날짜 상세 */}
              {calSelectedDay && (() => {
                const evts = getCalSchedules(calSelectedDay);
                const bkgs = getCalBookings(calSelectedDay);
                return (
                  <div style={{ padding:'8px 12px', borderTop:'1px solid var(--tblr-border-color)' }}>
                    <div className="d-flex align-items-center mb-1">
                      <strong style={{ fontSize:13 }}>{calMonth}/{calSelectedDay} 일정</strong>
                      <button className="btn btn-sm btn-ghost-secondary ms-auto px-1" onClick={() => setCalSelectedDay(null)}><IconX size={14} /></button>
                    </div>
                    {bkgs.map(b => {
                      const isMajor = b.gameType==='메이저';
                      return (
                        <div key={`b-${b.id}`} className="py-1 d-flex align-items-start justify-content-between" style={{ fontSize:13 }}>
                          <div style={{ flex:1, minWidth:0 }}>
                            <div className="d-flex align-items-center gap-2">
                              <IconFlag size={10} color={isMajor?'#ae3ec9':'#2fb344'} style={{flexShrink:0}} />
                              <strong style={{ overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',color:isMajor?'#ae3ec9':'#2fb344' }}>
                                {b.reservedTime?.slice(0,5)} {b.golfCourseName}
                              </strong>
                              <span className={`badge flex-shrink-0 ${isMajor?'bg-purple-lt':'bg-green-lt'}`} style={{fontSize:9}}>{b.gameType||'일반'}</span>
                            </div>
                            <div className="text-muted small ms-3">{b.playerCount}명{b.reserverName&&` · ${b.reserverName}`}{b.totalHoles&&` · ${b.totalHoles}홀`}</div>
                          </div>
                          <button className="btn btn-ghost-primary btn-sm px-1 flex-shrink-0" onClick={() => { setEditBooking(b); setBookingModal(true); }}><IconEdit size={14} /></button>
                        </div>
                      );
                    })}
                    {evts.map(s => (
                      <div key={s.id} className="py-1 d-flex align-items-start justify-content-between" style={{ fontSize:13 }}>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div className="d-flex align-items-center gap-2">
                            <span style={{ width:6,height:6,borderRadius:'50%',background:CAL_COLORS[s.id%CAL_COLORS.length],flexShrink:0 }} />
                            <strong style={{ overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{s.title}</strong>
                          </div>
                          {s.description && <div className="text-muted small ms-3" style={{ whiteSpace:'pre-line' }}>{s.description}</div>}
                        </div>
                        <div className="d-flex gap-0 flex-shrink-0">
                          <button className="btn btn-ghost-primary btn-sm px-1" onClick={() => { setEditSchedule(s); setScheduleForm({ title:s.title, description:s.description||'', startDate:s.startDate, endDate:s.endDate }); setScheduleModal(true); }}><IconEdit size={14} /></button>
                          <button className="btn btn-ghost-danger btn-sm px-1" onClick={() => handleScheduleDelete(s.id)}><IconTrash size={14} /></button>
                        </div>
                      </div>
                    ))}
                    {evts.length===0 && bkgs.length===0 && <div className="text-muted small">일정이 없습니다</div>}
                  </div>
                );
              })()}
            </div>
          </div>
        );
      })()}
      </div>

      {/* 일정 수정 모달 */}
      {scheduleModal && (
        <>
          <div className="modal-backdrop fade show" onMouseDown={onBackdropMouseDown} onClick={(e) => onBackdropClick(e, () => { setScheduleModal(false); setEditSchedule(null); })} />
          <div className="modal modal-blur fade show" style={{ display:'block' }} tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{editSchedule ? '일정 수정' : '일정 등록'}</h5>
                  <button className="btn-close" onClick={() => { setScheduleModal(false); setEditSchedule(null); }} />
                </div>
                <div className="modal-body">
                  <div className="mb-3"><label className="form-label">제목 <span className="text-danger">*</span></label>
                    <input className="form-control form-control-sm" value={scheduleForm.title} onChange={e => setScheduleForm(f=>({...f,title:e.target.value}))} maxLength={200} autoFocus /></div>
                  <div className="row mb-3"><div className="col-6"><label className="form-label">시작일</label>
                    <input type="date" className="form-control form-control-sm" value={scheduleForm.startDate} onChange={e => setScheduleForm(f=>({...f,startDate:e.target.value}))} /></div>
                  <div className="col-6"><label className="form-label">종료일</label>
                    <input type="date" className="form-control form-control-sm" value={scheduleForm.endDate} onChange={e => setScheduleForm(f=>({...f,endDate:e.target.value}))} /></div></div>
                  <div className="mb-3"><label className="form-label">설명</label>
                    <textarea className="form-control form-control-sm" rows={3} value={scheduleForm.description} onChange={e => setScheduleForm(f=>({...f,description:e.target.value}))} /></div>
                </div>
                <div className="modal-footer">
                  {editSchedule && <button className="btn btn-sm btn-danger me-auto" onClick={() => { handleScheduleDelete(editSchedule.id); setScheduleModal(false); setEditSchedule(null); }}><IconTrash size={14} className="me-1" />삭제</button>}
                  <button className="btn btn-sm" onClick={() => { setScheduleModal(false); setEditSchedule(null); }}>취소</button>
                  <button className="btn btn-sm btn-primary" onClick={handleScheduleSave} disabled={scheduleSaving}>{scheduleSaving ? '저장 중...' : editSchedule ? '수정' : '등록'}</button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* 부킹 수정 모달 */}
      {bookingModal && (
        <BookingFormModal
          onClose={() => { setBookingModal(false); setEditBooking(null); }}
          onSaved={() => { fetchCalData(calYear, calMonth); loadData(); }}
          showToast={showToast}
          apiPrefix="/golf"
          editData={editBooking}
        />
      )}

      {/* ── 개인별 통계 ── */}
      {members.length > 0 && (
        <div className="card mb-4">
          <div className="card-body py-3">
            {(() => {
              // 왕 계산
              const statsById = {};
              memberStats.forEach(s => { statsById[s.userId] = s; });
              let maxEagle = 0, maxBirdie = 0, maxPar = 0, maxBogey = 0;
              memberStats.forEach(s => {
                if (s.eagle > maxEagle) maxEagle = s.eagle;
                if (s.birdie > maxBirdie) maxBirdie = s.birdie;
                if (s.par > maxPar) maxPar = s.par;
                if (s.bogey > maxBogey) maxBogey = s.bogey;
              });

              return (
                <div className="d-flex member-stats-wrap" style={{ gap: 4, flexWrap: 'nowrap', justifyContent: 'center' }}>
                  {members.map(m => {
                    const st = statsById[m.id] || {};
                    const hasData = st.playCount > 0;
                    return (
                      <div key={m.id} className="member-stat-item" style={{ flex: '1 1 0', minWidth: 0, maxWidth: 140 }}
                        role={hasData ? 'button' : undefined}
                        onClick={() => { if (hasData) setStatGamesModal({ member: m, stat: st, games: st.recentGames || [] }); }}
                      >
                        <div className="text-center" style={{ cursor: hasData ? 'pointer' : 'default' }}>
                          {/* 타이틀 왕관 */}
                          <div className="member-stat-crown" style={{ fontSize: 9, lineHeight: 1.4, minHeight: 28, marginBottom: 1 }}>
                            {hasData && st.eagle > 0 && st.eagle === maxEagle && (
                              <div style={{ color: '#ab9046', whiteSpace: 'nowrap' }}><IconCrown size={9} style={{ verticalAlign: '-1px' }} /> 이글왕({st.eagle})</div>
                            )}
                            {hasData && st.birdie > 0 && st.birdie === maxBirdie && (
                              <div style={{ color: '#c41000', whiteSpace: 'nowrap' }}><IconCrown size={9} style={{ verticalAlign: '-1px' }} /> 버디왕({st.birdie})</div>
                            )}
                            {hasData && st.par > 0 && st.par === maxPar && (
                              <div style={{ color: '#078f00', whiteSpace: 'nowrap' }}><IconCrown size={9} style={{ verticalAlign: '-1px' }} /> 파왕({st.par})</div>
                            )}
                            {hasData && st.bogey > 0 && st.bogey === maxBogey && (
                              <div style={{ color: '#00078f', whiteSpace: 'nowrap' }}><IconCrown size={9} style={{ verticalAlign: '-1px' }} /> 양파왕({st.bogey})</div>
                            )}
                            {hasData && !(
                              (st.eagle > 0 && st.eagle === maxEagle) || (st.birdie > 0 && st.birdie === maxBirdie) ||
                              (st.par > 0 && st.par === maxPar) || (st.bogey > 0 && st.bogey === maxBogey)
                            ) && (
                              <div className="text-muted" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 9 }}>
                                {st.birdie > 0 ? `버디(${st.birdie})` : ''} {st.par > 0 ? `파(${st.par})` : ''}
                              </div>
                            )}
                          </div>

                          {/* 아바타 */}
                          {m.profileImage ? (
                            <img src={m.profileImage} alt={m.nickname || m.name} className="member-stat-avatar" style={{
                              width: 52, height: 52, borderRadius: '50%', margin: '0 auto 4px',
                              objectFit: 'cover', display: 'block', maxWidth: '100%',
                              border: `2.5px solid ${m.color || 'var(--tblr-primary)'}`,
                            }} />
                          ) : (
                            <div className="member-stat-avatar" style={{
                              width: 52, height: 52, borderRadius: '50%', margin: '0 auto 4px',
                              background: m.bgcolor || m.color || 'var(--tblr-primary)',
                              color: m.bgcolor ? (m.color || '#fff') : '#fff',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: 18, fontWeight: 700, maxWidth: '100%',
                              border: `2.5px solid ${m.color || 'var(--tblr-primary)'}`,
                            }}>
                              <span className="member-stat-avatar-text">{(m.nickname || m.name)?.[0]?.toUpperCase() || '?'}</span>
                            </div>
                          )}

                          {/* 이름 + 평균 */}
                          <div className="member-stat-name" style={{ fontWeight: 700, fontSize: 11, color: mc(m), whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {m.nickname || m.name}
                          </div>
                          {hasData ? (
                            <>
                              <div className="d-flex justify-content-center member-stat-best gap-1 mt-1" style={{ fontSize: 9 }}>
                                <span className="d-inline-flex align-items-center gap-1" style={{ color: '#e03131' }}>
                                  <span className="d-none d-md-inline"><IconThumbUp size={9} /></span> {st.bestScore}
                                </span>
                                <span className="d-inline-flex align-items-center gap-1" style={{ color: '#1971c2' }}>
                                  <span className="d-none d-md-inline"><IconThumbDown size={9} /></span> {st.worstScore}
                                </span>
                              </div>
                              <div className="member-stat-score" style={{ fontSize: 10, fontWeight: 700, marginTop: 1, whiteSpace: 'nowrap', color: mc(m) }}>
                                {Math.round(st.avgScore)}타 / {st.playCount}회
                              </div>
                              {/* 제외 전 전체(N+2) 정보 */}
                              {gameCount && st.allPlayCount > st.playCount && (
                                <div className="member-stat-sub" style={{
                                  marginTop: 3, padding: '1px 3px', borderRadius: 4, fontSize: 9,
                                  background: 'var(--tblr-bg-surface-secondary, #f8f9fa)',
                                  border: '1px solid var(--tblr-border-color, #e6e7e9)',
                                }}>
                                  <div style={{ color: '#888' }}>ALL:{Math.round(st.allAvgScore)}타 / {st.allPlayCount}회</div>
                                </div>
                              )}
                              <a
                                href="#trend"
                                className="btn btn-sm btn-outline-secondary mt-1"
                                style={{ fontSize: 9, padding: '1px 6px', lineHeight: 1.5 }}
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setTrendModal({ member: m }); }}
                              >
                                추세보기
                              </a>
                            </>
                          ) : (
                            <div className="text-muted small mt-1">기록 없음</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* 개인별 통계 근거 라운드 리스트 팝업 */}
      {statGamesModal && (() => {
        const m = statGamesModal.member;
        const st = statGamesModal.stat || {};
        const color = mc(m);
        return (
          <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.55)' }} onClick={() => setStatGamesModal(null)}>
            <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
              <div className="modal-content" style={{ borderRadius: 14, overflow: 'hidden', border: 'none', boxShadow: '0 12px 32px rgba(0,0,0,0.25)' }}>
                {/* 헤더: 멤버 색상 그라데이션 배경 + 아바타 + 요약 통계 */}
                <div style={{
                  position: 'relative',
                  padding: '20px 44px 16px 16px',
                  background: `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)`,
                  color: '#fff',
                }}>
                  <button
                    className="btn btn-icon btn-sm"
                    onClick={() => setStatGamesModal(null)}
                    style={{
                      position: 'absolute', top: 10, right: 10, zIndex: 2,
                      background: 'rgba(255,255,255,0.25)', border: 'none', color: '#fff',
                      width: 28, height: 28, borderRadius: '50%',
                    }}
                  >
                    <IconX size={16} />
                  </button>
                  <div className="d-flex align-items-center" style={{ gap: 12 }}>
                    {m.profileImage ? (
                      <img src={m.profileImage} alt={m.nickname || m.name} style={{
                        width: 48, height: 48, borderRadius: '50%', objectFit: 'cover',
                        border: '2.5px solid rgba(255,255,255,0.9)', flexShrink: 0,
                      }} />
                    ) : (
                      <div style={{
                        width: 48, height: 48, borderRadius: '50%', flexShrink: 0,
                        background: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', fontSize: 18, fontWeight: 700,
                        border: '2.5px solid rgba(255,255,255,0.9)',
                      }}>
                        {(m.nickname || m.name)?.[0]?.toUpperCase() || '?'}
                      </div>
                    )}
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 16, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {m.nickname || m.name}
                      </div>
                      {st.playCount > 0 && (
                        <div style={{ fontSize: 12, opacity: 0.9, marginTop: 2 }}>
                          평균 {Math.round(st.avgScore)}타 · {st.playCount}회
                          {st.allPlayCount > st.playCount && (
                            <span style={{ marginLeft: 6, opacity: 0.85 }}>(평균 {Math.round(st.allAvgScore)}타 · {st.allPlayCount}회)</span>
                          )}
                          <span style={{ marginLeft: 8 }}>
                            <IconThumbUp size={11} style={{ verticalAlign: '-1px' }} /> {st.bestScore}
                            <span style={{ marginLeft: 6 }}><IconThumbDown size={11} style={{ verticalAlign: '-1px' }} /> {st.worstScore}</span>
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="modal-body" style={{ padding: '14px 16px' }}>
                  {statGamesModal.games.length === 0 ? (
                    <div className="text-muted text-center py-3">라운드 기록이 없습니다.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {statGamesModal.games.map(g => {
                        const dateInfo = formatDateWithDay(g.reservedDate);
                        return (
                          <div
                            key={g.reservationId}
                            onClick={() => openScoreCardFromStat(g.reservationId)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 10,
                              padding: '9px 10px', borderRadius: 10, cursor: 'pointer',
                              background: g.excluded ? 'var(--tblr-bg-surface-secondary, #f8f9fa)' : '#fff',
                              border: `1px solid ${g.excluded ? 'var(--tblr-border-color, #e6e7e9)' : color + '33'}`,
                              opacity: g.excluded ? 0.65 : 1,
                            }}
                          >
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div style={{ fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap' }}>
                                {dateInfo.text}
                                <span style={{ color: dateInfo.dayColor, marginLeft: 2 }}>({dateInfo.day})</span>
                                <span className="text-muted" style={{ marginLeft: 8, fontWeight: 400 }}>{g.reservedTime?.slice(0, 5) || '-'}</span>
                              </div>
                              <div className="text-muted" style={{ fontSize: 11.5, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {g.golfCourseName || '-'}
                              </div>
                            </div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: g.excluded ? '#adb5bd' : color, flexShrink: 0 }}>
                              {g.score}{g.excluded && <span style={{ fontSize: 11, marginLeft: 3 }}>(X)</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="text-muted mt-2" style={{ fontSize: 11 }}>(X)는 최고·최저 제외되어 평균에서 빠진 라운드입니다.</div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* 개인별 핸디 추세보기 팝업 */}
      {trendModal && (
        <GolfTrendModal
          member={trendModal.member}
          initialYear={year}
          initialGameType={filterGameType}
          initialCount={gameCount}
          color={mc(trendModal.member)}
          onClose={() => setTrendModal(null)}
        />
      )}

      {/* 전체 멤버 추세 비교 팝업 */}
      {allTrendModal && (
        <GolfAllTrendModal
          members={members}
          initialYear={year}
          initialGameType={filterGameType}
          initialCount={gameCount}
          colorFor={mc}
          onClose={() => setAllTrendModal(false)}
        />
      )}

      {/* 골프 예약 목록 */}
      <div className="card">
        <div className="card-header d-block py-2" style={{ overflow: 'visible' }}>
          <div className="d-flex align-items-center gap-2 mb-2">
            <IconGolf size={18} />
            <h3 className="card-title mb-0">골프 예약</h3>
            <span className="badge bg-blue-lt">{totalElements.toLocaleString()}건</span>
            <button className="btn btn-sm btn-ghost-secondary d-flex align-items-center" title={compact ? '확대' : '축소'}
              onClick={() => setCompact(v => !v)} style={{ padding: '2px 4px' }}>
              {compact ? <IconArrowsMaximize size={16} /> : <IconArrowsMinimize size={16} />}
            </button>
            <button className="btn btn-sm btn-primary d-flex align-items-center gap-1"
              onClick={openNewScoreCard}>
              <IconPlus size={14} /> 추가
            </button>
            <button className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
              onClick={() => setAllTrendModal(true)}>
              <IconChartBar size={14} /> 그래프
            </button>
          </div>
          <div className="d-flex gap-2 align-items-center flex-wrap w-100">
            <MultiSelect
              options={courses.map(c => ({ value: c.id, label: c.name }))}
              selected={selectedCourseIds}
              onChange={updateFilter(setSelectedCourseIds)}
              placeholder="전체 골프장"
            />
            <select className="form-select form-select-sm" style={{ width: 'auto', minWidth: 80 }}
              value={gameCount} onChange={e => {
                setGameCount(e.target.value);
                if (!e.target.value) setRowFilters(prev => { const n = new Set(prev); n.delete('included'); n.delete('excluded'); return n; });
              }}>
              <option value="">경기수</option>
              {[5, 10, 20, 30, 50, 100].map(n => <option key={n} value={n}>최근 {n}</option>)}
            </select>
            <select className="form-select form-select-sm" style={{ width: 'auto', minWidth: 65 }}
              value={year} onChange={e => setYear(e.target.value)}>
              <option value="">년도</option>
              {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <select className="form-select form-select-sm" style={{ width: 'auto', minWidth: 70 }}
              value={filterGameType} onChange={e => setFilterGameType(e.target.value)}>
              <option value="">유형</option>
              <option value="일반">일반</option>
              <option value="메이저">메이저</option>
            </select>
<button className={`btn btn-sm ${showLegend ? 'btn-secondary' : 'btn-outline-secondary'}`} title="범례"
              onClick={() => setShowLegend(v => !v)}>
              ?
            </button>
          </div>
        </div>
        {/* 범례 필터 */}
        {showLegend && (() => {
          const filters = [
            { key: 'today',   label: '오늘',   bg: '#fffce6', border: '#e6d98e' },
            { key: 'future',  label: '미래',   bg: '#e8f4fd', border: '#b0d4f1' },
            { key: 'valid',   label: '유효=Y', bg: '#f0fdf4', border: '#86efac' },
            { key: 'invalid', label: '유효=N', bg: '#f4f4f5', border: '#ccc' },
          ];
          if (gameCount) {
            filters.push({ key: 'included', label: '경기수 포함', bg: '#f0fdf4', border: '#86efac' });
            filters.push({ key: 'excluded', label: '✕ 최고/최저 제외', bg: '#fef9ef', border: '#f0c674' });
          }
          return (
            <div className="px-3 pb-2" style={{ fontSize: 12 }}>
              <div className="d-flex flex-wrap gap-2">
                {filters.map(f => {
                  const active = rowFilters.has(f.key);
                  return (
                    <button key={f.key} type="button"
                      className="btn btn-sm d-inline-flex align-items-center gap-1"
                      style={{
                        padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: active ? 600 : 400,
                        background: active ? f.bg : 'transparent',
                        border: `1.5px solid ${active ? f.border : '#ddd'}`,
                        color: active ? '#333' : '#888',
                      }}
                      onClick={() => toggleRowFilter(f.key)}>
                      <span style={{ display:'inline-block',width:10,height:10,borderRadius:2,background:f.bg,border:`1px solid ${f.border}` }} />
                      {f.label}
                    </button>
                  );
                })}
              </div>
              {gameCount && <div className="text-muted mt-1">경기수 {gameCount}: 멤버별 최근 {Number(gameCount)+2}경기 중 최고·최저 제외 → {gameCount}경기 평균</div>}
            </div>
          );
        })()}
        <div className="table-responsive">
          <table className="table table-vcenter table-hover card-table" style={{ fontSize: compact ? 12 : 13 }}>
            <thead>
              {compact ? (
              <tr>
                <th style={{ padding: '4px 6px' }}>날짜</th>
                <th style={{ padding: '4px 6px' }}>골프장</th>
                {members.map(m => (
                  <th key={m.id} style={{ padding: '4px 4px', textAlign: 'center', color: mc(m), fontSize: 11, maxWidth: 50 }}>
                    {(m.nickname || m.name)?.slice(0, 2)}
                  </th>
                ))}
              </tr>
              ) : (
              <tr>
                <th style={{ width: 50, textAlign: 'center' }}>No</th>
                <th style={{ width: 120 }}>날짜</th>
                <th style={{ width: 60, textAlign: 'center' }}>티업</th>
                <th>골프장</th>
                {members.map(m => (
                  <th key={m.id} style={{ width: 70, textAlign: 'center', color: mc(m) }}>
                    {m.nickname || m.name}
                  </th>
                ))}
              </tr>
              )}
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={(compact ? 2 : 4) + members.length} className="text-center py-4 text-muted">
                  <span className="spinner-border spinner-border-sm me-2" />불러오는 중...
                </td></tr>
              ) : reservations.length === 0 ? (
                <tr><td colSpan={(compact ? 2 : 4) + members.length} className="text-center py-4 text-muted">예약 내역이 없습니다.</td></tr>
              ) : (() => {
                const todayStr = new Date().toLocaleDateString('sv');
                const hasRowFilter = rowFilters.size > 0;

                // 행 필터 적용: 선택된 조건에 해당하는 행만 표시
                const filtered = hasRowFilter ? reservations.filter(r => {
                  const isToday = r.reservedDate === todayStr;
                  const isFuture = r.reservedDate > todayStr;
                  const isInvalid = r.useYn === 'N';
                  // 경기수 포함/제외는 멤버 중 1명이라도 해당하면 표시
                  const anyIncluded = gameCount && members.some(m => {
                    const gm = memberGameMap[m.id];
                    return gm && gm.included.has(r.id);
                  });
                  const anyExcluded = gameCount && members.some(m => {
                    const gm = memberGameMap[m.id];
                    return gm && gm.excluded.has(r.id);
                  });

                  if (rowFilters.has('today') && isToday) return true;
                  if (rowFilters.has('future') && isFuture) return true;
                  if (rowFilters.has('valid') && r.useYn === 'Y') return true;
                  if (rowFilters.has('invalid') && isInvalid) return true;
                  if (rowFilters.has('included') && anyIncluded) return true;
                  if (rowFilters.has('excluded') && anyExcluded) return true;
                  return false;
                }) : reservations;

                return filtered.map((r, idx) => {
                const dateInfo = formatDateWithDay(r.reservedDate);
                const courseSections = r.sections?.map(s => s.sectionName).join(' > ') || '';
                const isToday = r.reservedDate === todayStr;
                const isFuture = r.reservedDate > todayStr;
                const isInvalid = r.useYn === 'N';

                // 행 배경색 우선순위: 무효 > 오늘 > 미래
                let rowBg = undefined;
                if (isInvalid) rowBg = '#f4f4f5';
                else if (isToday) rowBg = '#fffce6';
                else if (isFuture) rowBg = '#e8f4fd';

                const rowClass = isInvalid ? 'res-row-invalid' : isToday ? 'res-row-today' : isFuture ? 'res-row-future' : '';
                return compact ? (
                  <tr key={r.id} className={rowClass} style={{ background: rowBg }}>
                    <td style={{ padding: '3px 6px', whiteSpace: 'nowrap', cursor: 'pointer', fontSize: 11 }}
                      onClick={() => openScoreCard(r)}>
                      <span style={{ color: dateInfo.dayColor || 'var(--tblr-body-color)' }}>{dateInfo.text}</span>
                      <span style={{ color: dateInfo.dayColor, fontWeight: 600 }}>({dateInfo.day})</span>
                    </td>
                    <td style={{ padding: '3px 6px', fontSize: 11, maxWidth: 60, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.golfCourseName?.slice(0, 4)}
                    </td>
                    {members.map(m => {
                      const score = findScoreForMember(r, m.id);
                      const gm = memberGameMap[m.id];
                      const hasScore = score && score.totalScore != null;
                      const isIncluded = hasScore && gm && gm.included.has(r.id);
                      const isExcluded = hasScore && gm && gm.excluded.has(r.id);
                      let cellBg = undefined;
                      if (gameCount && isIncluded) cellBg = isExcluded ? '#fef9ef' : '#f0fdf4';
                      return (
                        <td key={m.id} className="text-center" style={{ padding: '3px 2px', fontSize: 11, background: cellBg }}>
                          {hasScore ? (<>
                            <span style={{ fontWeight: 700, color: mc(m) }}>
                              {score.totalScore}
                              {isExcluded && <span style={{ color: '#d63939', fontSize: 9 }}>✕</span>}
                            </span>
                            {score.totalPutting > 0 && <div style={{ fontSize: 9, color: '#888' }}>P{score.totalPutting}</div>}
                          </>) : <span style={{ color: '#ddd' }}>-</span>}
                        </td>
                      );
                    })}
                  </tr>
                ) : (
                  <tr key={r.id} className={rowClass} style={{ background: rowBg }}>
                    <td className="text-center text-muted">{totalElements - reservations.indexOf(r)}</td>
                    <td style={{ whiteSpace: 'nowrap', cursor: 'pointer' }}
                      onClick={() => openScoreCard(r)}>
                      <span className="fw-medium" style={{ color: dateInfo.dayColor || 'var(--tblr-body-color)', textDecoration: 'underline', cursor: 'pointer' }}>{dateInfo.text}</span>
                      <span style={{ color: dateInfo.dayColor, fontWeight: 600, marginLeft: 2 }}>({dateInfo.day})</span>
                    </td>
                    <td className="text-center fw-medium">{r.reservedTime?.slice(0, 5) || '-'}</td>
                    <td style={{ minWidth: 160 }}>
                      <div style={{ whiteSpace: 'nowrap' }}>
                        <span className="fw-medium">{r.golfCourseName}</span>
                        {courseSections && <span className="text-muted" style={{ fontSize: 11 }}> ({courseSections})</span>}
                        {r.sections?.length > 0 && <span className="ms-1"><HoleBadge sections={r.sections} /></span>}
                        {r.weather && <span className="ms-1"><WeatherBadge code={r.weather} temperature={r.temperature} /></span>}
                        {r.photos?.length > 0 && <IconPhoto size={12} className="ms-1" color="#206bc4" title={`사진 ${r.photos.length}장`} />}
                      </div>
                      <div style={{ whiteSpace: 'nowrap' }}>
                        {r.greenFee != null && <span className="text-muted me-1" style={{ fontSize: 11 }}>&#8361;{r.greenFee}</span>}
                        {r.gameType && <span className={`badge me-1 ${r.gameType === '메이저' ? 'bg-purple-lt' : 'bg-azure-lt'}`} style={{ fontSize: 10 }}>{r.gameType}</span>}
                        {r.memo && <span className="text-muted" style={{ fontSize: 11 }}>{r.memo}</span>}
                      </div>
                    </td>
                    {members.map(m => {
                      const score = findScoreForMember(r, m.id);
                      const gm = memberGameMap[m.id];
                      const hasScore = score && score.totalScore != null;
                      const isIncluded = hasScore && gm && gm.included.has(r.id);
                      const isExcluded = hasScore && gm && gm.excluded.has(r.id);

                      // 셀 배경: 경기수 포함이면 연초록 (제외된 것도 포함 표시는 됨)
                      let cellBg = undefined;
                      if (gameCount && isIncluded) cellBg = isExcluded ? '#fef9ef' : '#f0fdf4';

                      const cellClass = gameCount && isIncluded ? (isExcluded ? 'res-cell-excluded' : 'res-cell-included') : '';
                      return (
                        <td key={m.id} className={`text-center ${cellClass}`} style={{ fontSize: 13, background: cellBg }}>
                          {hasScore ? (<>
                            <span style={{ fontWeight: 700, color: mc(m) }}>
                              {score.totalScore}
                              {isExcluded && (
                                <span style={{ color: '#d63939', marginLeft: 2, fontSize: 11 }} title="최고/최저 제외">✕</span>
                              )}
                            </span>
                            {score.totalPutting > 0 && <div style={{ fontSize: 10, color: '#888' }}>P{score.totalPutting}</div>}
                          </>) : (
                            <span style={{ color: '#ddd' }}>-</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                  );
              });
              })()}
            </tbody>
          </table>
        </div>

      </div>

      {/* 홈 스코어카드 모달 */}
      {scoreReservation && (
        <HomeScoreCardModal
          reservation={scoreReservation}
          scores={scoreData}
          loading={scoreLoading}
          members={members}
          onClose={closeScoreCard}
          onSave={handleSaveScores}
          initialEdit={newCardOpen}
          onDelete={!newCardOpen && scoreReservation?.id ? handleDeleteScoreReservation : undefined}
          onPhotosChange={handlePhotosChange}
        />
      )}
    </>
  );
}

// ── 홈 스코어카드 모달 (레거시 스타일) ──
export function HomeScoreCardModal({ reservation, scores, loading, members, onClose, onSave, initialEdit, onDelete, onPhotosChange }) {
  const cardRef = useRef(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addMemberId, setAddMemberId] = useState('');
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(!!initialEdit);
  const [photos, setPhotos] = useState(reservation.photos || []);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [editScores, setEditScores] = useState(initialEdit ? JSON.parse(JSON.stringify(scores)) : []);
  const [manualScores, setManualScores] = useState(() => {
    const init = {};
    (initialEdit ? scores : []).forEach(s => { if (s.totalScore != null) init[s.playerSlot] = s.totalScore; });
    return init;
  });
  const [localDate, setLocalDate] = useState(reservation.reservedDate || '');
  const [localTime, setLocalTime] = useState(reservation.reservedTime?.slice(0, 5) || '');
  const [localWeather, setLocalWeather] = useState(reservation.weather || '');
  const [localTemp, setLocalTemp] = useState(reservation.temperature || '');
  const [localHoles, setLocalHoles] = useState(reservation.totalHoles || '');
  const [localGreenFee, setLocalGreenFee] = useState(reservation.greenFee ?? '');
  const [localMemo, setLocalMemo] = useState(reservation.memo || '');
  const [localGameType, setLocalGameType] = useState(reservation.gameType || '일반');
  const [localUseYn, setLocalUseYn] = useState(reservation.useYn || 'Y');

  // 골프장/코스
  const [courseList, setCourseList] = useState([]);
  const [localCourseId, setLocalCourseId] = useState(reservation.golfCourseId || '');
  const [localCourseName, setLocalCourseName] = useState(reservation.golfCourseName || '');
  const [courseInput, setCourseInput] = useState(reservation.golfCourseName || '');
  const [showCourseList, setShowCourseList] = useState(false);
  const courseDropRef = useRef(null);
  const [sectionList, setSectionList] = useState([]);
  const [localSections, setLocalSections] = useState(
    (reservation.sections || []).map((s, i) => ({ sectionId: s.sectionId, playOrder: i + 1, sectionName: s.sectionName, holesCount: s.holesCount, parData: s.parData }))
  );

  useEffect(() => {
    if (editing && courseList.length === 0) {
      api.get('/golf/courses').then(res => setCourseList(res.data?.content || res.data || [])).catch(() => {});
    }
  }, [editing, courseList.length]);

  useEffect(() => {
    if (editing && localCourseId) {
      api.get(`/golf/courses/${localCourseId}/sections`).then(res => setSectionList(res.data || [])).catch(() => setSectionList([]));
    }
  }, [editing, localCourseId]);

  const filteredCourses = courseInput
    ? courseList.filter(c => c.name.toLowerCase().includes(courseInput.toLowerCase())).slice(0, 30)
    : courseList.slice(0, 30);

  const selectCourse = (c) => {
    setCourseInput(c.name);
    setLocalCourseId(c.id);
    setLocalCourseName(c.name);
    setShowCourseList(false);
    setLocalSections([]);
  };

  useEffect(() => {
    const handler = (e) => { if (courseDropRef.current && !courseDropRef.current.contains(e.target)) setShowCourseList(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // 코스 변경 시 홀 수 자동 계산
  useEffect(() => {
    if (!editing) return;
    if (localSections.length === 0) return;
    const h0 = localSections[0]?.holesCount || 9;
    if (localSections.length === 1) { setLocalHoles(String(h0)); return; }
    const h1 = localSections[1]?.holesCount || 9;
    if (localSections[0]?.sectionId === localSections[1]?.sectionId) setLocalHoles(String(h0 * 2));
    else setLocalHoles(String(h0 + h1));
  }, [editing, localSections]);

  const toggleSection = (sec) => {
    setLocalSections(prev => {
      const exists = prev.find(s => s.sectionId === sec.id);
      if (exists) return prev.filter(s => s.sectionId !== sec.id);
      return [...prev, { sectionId: sec.id, playOrder: prev.length + 1, sectionName: sec.name, holesCount: sec.holesCount, parData: sec.parData }];
    });
  };

  const [localStatus, setLocalStatus] = useState(reservation.status || 'PENDING');

  // 스크라치
  const scratchRef = useRef(null);
  const [scratchSettingsOpen, setScratchSettingsOpen] = useState(false);
  const [scratchResultOpen, setScratchResultOpen] = useState(false);
  const [scratchData, setScratchData] = useState(null);
  const [scratchConfig, setScratchConfig] = useState(() => {
    try { return reservation.bettingConfigJson ? JSON.parse(reservation.bettingConfigJson) : null; } catch { return null; }
  });

  useEffect(() => {
    if (scratchConfig && scores.length > 0 && sections.length > 0) {
      const data = calculateScratch(scratchConfig, scores, sections);
      setScratchData(data);
    }
  }, [scores, scratchConfig]); // eslint-disable-line

  const toggleGameType = () => setLocalGameType(prev => prev === '메이저' ? '일반' : '메이저');
  const toggleUseYn = () => setLocalUseYn(prev => prev === 'Y' ? 'N' : 'Y');

  const handleDelete = async (target) => {
    if (!window.confirm(`${target.playerName || 'P' + target.playerSlot}을(를) 삭제하시겠습니까?`)) return;
    const updated = scores.filter(s =>
      s.id ? s.id !== target.id : s.playerSlot !== target.playerSlot
    );
    setSaving(true);
    await onSave(updated);
    setSaving(false);
  };

  // 편집 모드 진입
  const startEdit = () => {
    const initScores = JSON.parse(JSON.stringify(scores));
    setEditScores(initScores);
    const initManual = {};
    initScores.forEach(s => { if (s.totalScore != null) initManual[s.playerSlot] = s.totalScore; });
    setManualScores(initManual);
    setEditing(true);
  };

  /** 사진 추가: 클라이언트에서 400px(원본)/80px(썸네일)로 리사이즈 후 업로드 */
  const handlePhotoSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0 || !reservation.id) return;

    const remaining = 6 - (photos.length || 0);
    if (files.length > remaining) {
      alert(`사진은 최대 6장까지 등록할 수 있습니다. (${remaining}장 추가 가능)`);
      return;
    }

    setPhotoUploading(true);
    try {
      const formData = new FormData();
      for (const file of files) {
        const [fullBlob, thumbBlob] = await Promise.all([
          resizeImageFile(file, 400),
          resizeImageFile(file, 80),
        ]);
        const baseName = (file.name || 'photo').replace(/\.[^./\\]+$/, '');
        formData.append('full', fullBlob, `${baseName}.jpg`);
        formData.append('thumb', thumbBlob, `${baseName}_thumb.jpg`);
      }
      const res = await api.post(`/golf/reservations/${reservation.id}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const newPhotos = res.data;
      setPhotos(prev => {
        const updated = [...prev, ...newPhotos];
        reservation.photos = updated;
        onPhotosChange?.(updated);
        return updated;
      });
    } catch (err) {
      alert(err?.response?.data?.error || '사진 업로드에 실패했습니다.');
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('사진을 삭제하시겠습니까?')) return;
    try {
      await api.delete(`/golf/reservations/${reservation.id}/photos/${photoId}`);
      setPhotos(prev => {
        const updated = prev.filter(p => p.id !== photoId);
        reservation.photos = updated;
        onPhotosChange?.(updated);
        return updated;
      });
    } catch {
      alert('사진 삭제에 실패했습니다.');
    }
  };

  const cancelEdit = () => {
    setEditing(false);
    setEditScores([]);
    setManualScores({});
    setLocalDate(reservation.reservedDate || '');
    setLocalTime(reservation.reservedTime?.slice(0, 5) || '');
    setLocalWeather(reservation.weather || '');
    setLocalTemp(reservation.temperature || '');
    setLocalHoles(reservation.totalHoles || '');
    setLocalGreenFee(reservation.greenFee ?? '');
    setLocalMemo(reservation.memo || '');
    setLocalGameType(reservation.gameType || '일반');
    setLocalUseYn(reservation.useYn || 'Y');
    setLocalStatus(reservation.status || 'PENDING');
    setLocalCourseId(reservation.golfCourseId || '');
    setLocalCourseName(reservation.golfCourseName || '');
    setCourseInput(reservation.golfCourseName || '');
    setLocalSections((reservation.sections || []).map((s, i) => ({ sectionId: s.sectionId, playOrder: i + 1, sectionName: s.sectionName, holesCount: s.holesCount, parData: s.parData })));
  };

  const handleSaveEdit = async () => {
    const sections = localSections || [];
    const updated = editScores.map(s => {
      let json = {};
      try {
        if (s.holeScoresJson) {
          const parsed = typeof s.holeScoresJson === 'string' ? JSON.parse(s.holeScoresJson) : s.holeScoresJson;
          json = parsed || {};
        }
      } catch { json = {}; }
      let totalOver = 0, totalPutting = 0, totalPar = 0;
      sections.forEach((sec, idx) => {
        const pars = parsePar(sec.parData);
        const holeData = json[`no${idx + 1}`] || {};
        const puttData = json[`putting${idx + 1}`] || {};
        for (let h = 1; h <= (sec.holesCount || 9); h++) {
          totalOver += Number(holeData[String(h)] || 0);
          totalPar += (pars[h - 1] || 0);
          totalPutting += Number(puttData[String(h)] || 0);
        }
      });
      const manualTotal = manualScores[s.playerSlot] != null && manualScores[s.playerSlot] !== '' ? Number(manualScores[s.playerSlot]) : null;
      return {
        ...s,
        totalScore: manualTotal != null ? manualTotal : totalPar + totalOver,
        overPar: manualTotal != null ? manualTotal - totalPar : totalOver,
        totalPutting,
        holeScoresJson: typeof s.holeScoresJson === 'object' ? JSON.stringify(s.holeScoresJson) : s.holeScoresJson,
      };
    });
    setSaving(true);
    // 예약 정보 저장
    try {
      const payload = {
        reservedDate: localDate,
        reservedTime: localTime ? localTime + ':00' : null,
        weather: localWeather || null,
        temperature: localTemp || null,
        greenFee: localGreenFee !== '' ? localGreenFee : null,
        memo: localMemo || null,
        gameType: localGameType,
        useYn: localUseYn,
        status: localStatus,
      };
      if (localCourseId) {
        payload.golfCourseId = Number(localCourseId);
      }
      if (localSections.length > 0) {
        payload.sections = localSections.map((s, i) => ({ sectionId: s.sectionId, playOrder: i + 1 }));
      }

      if (!reservation.id) {
        // 신규 생성
        if (!localCourseId) { alert('골프장을 선택해주세요.'); setSaving(false); return; }
        payload.playerCount = updated.length || 4;
        payload.status = 'PENDING';
        const createRes = await api.post('/golf/reservations', payload);
        reservation.id = createRes.data.id;
        reservation.golfCourseName = localCourseName;
      } else {
        // 기존 수정
        await api.put(`/golf/reservations/${reservation.id}`, payload);
      }
      // 로컬 reservation 객체도 갱신
      reservation.reservedDate = localDate;
      reservation.reservedTime = localTime ? localTime + ':00' : null;
      reservation.weather = localWeather;
      reservation.temperature = localTemp;
      reservation.greenFee = localGreenFee !== '' ? localGreenFee : null;
      reservation.memo = localMemo || null;
      reservation.gameType = localGameType;
      reservation.useYn = localUseYn;
      reservation.status = localStatus;
      reservation.golfCourseId = localCourseId;
      reservation.golfCourseName = localCourseName;
    } catch {}
    await onSave(updated);
    setSaving(false);
    setEditing(false);
    setEditScores([]);
  };

  const updateHoleScore = (slotIdx, secIdx, hole, value) => {
    setEditScores(prev => {
      const next = [...prev];
      const s = { ...next[slotIdx] };
      let json = s.holeScoresJson ? (typeof s.holeScoresJson === 'string' ? JSON.parse(s.holeScoresJson) : { ...s.holeScoresJson }) : {};
      const key = `no${secIdx + 1}`;
      json[key] = { ...(json[key] || {}), [String(hole)]: value };
      s.holeScoresJson = json;
      next[slotIdx] = s;
      return next;
    });
  };

  const updatePutting = (slotIdx, secIdx, hole, value) => {
    setEditScores(prev => {
      const next = [...prev];
      const s = { ...next[slotIdx] };
      let json = s.holeScoresJson ? (typeof s.holeScoresJson === 'string' ? JSON.parse(s.holeScoresJson) : { ...s.holeScoresJson }) : {};
      const key = `putting${secIdx + 1}`;
      json[key] = { ...(json[key] || {}), [String(hole)]: value };
      s.holeScoresJson = json;
      next[slotIdx] = s;
      return next;
    });
  };

  const parsePar = (parData) => {
    if (!parData) return [];
    try { return JSON.parse(parData); } catch {
      // "5,4,3,4,5,3,4,4,4" 형태 지원
      return parData.split(',').map(v => Number(v.trim())).filter(v => !isNaN(v));
    }
  };

  const parseHoles = (jsonStr, key) => {
    if (!jsonStr) return {};
    try {
      const obj = typeof jsonStr === 'string' ? JSON.parse(jsonStr) : jsonStr;
      return obj[key] || {};
    } catch { return {}; }
  };

  // 스코어 셀 꾸미기: 레거시 스타일 참고
  const ScoreCell = ({ value, par }) => {
    if (value === '' || value == null) return <span className="score-empty" style={{ color: '#ccc' }}>-</span>;
    const v = Number(value);
    if (isNaN(v)) return <span className="score-empty" style={{ color: '#ccc' }}>-</span>;

    const isDoublePar = par > 0 && v === par;
    const base = {
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 20, height: 20, fontSize: 12, lineHeight: 1, textAlign: 'center',
    };

    if (v <= -2)
      return <span className="score-birdie" style={{ ...base, border: 'double 3px #dd4b39', borderRadius: '50%', fontWeight: 'bold', color: '#dd4b39' }}>{v}</span>;
    if (v === -1)
      return <span className="score-birdie" style={{ ...base, border: 'solid 1px #dd4b39', borderRadius: '50%', color: '#dd4b39' }}>{v}</span>;
    if (v === 0)
      return <span style={{ ...base }}>{v}</span>;
    if (isDoublePar)
      return <span className="score-yangpa" style={{ ...base, background: '#555', color: '#fff' }}>{v}</span>;
    if (v === 1)
      return <span className="score-bogey" style={{ ...base, border: 'solid 1px #337ab7', color: '#337ab7' }}>{v}</span>;
    if (v === 2)
      return <span className="score-bogey" style={{ ...base, border: 'double 3px #337ab7', color: '#337ab7' }}>{v}</span>;
    return <span className="score-over" style={{ ...base, background: '#ccc' }}>{v}</span>;
  };

  const sections = localSections;
  const currentScores = editing ? editScores : scores;
  const displayScores = currentScores.filter(s => s.joinYn !== 'N');

  const totalCoursePar = sections.reduce((sum, sec) => {
    const pars = parsePar(sec.parData);
    return sum + pars.reduce((a, b) => a + b, 0);
  }, 0);

  // 멤버 매핑 (프로필 이미지, 색상 등)
  const memberMap = {};
  (members || []).forEach(m => { memberMap[m.id] = m; });

  // 1등(최저 스코어) 멤버의 color 계열 그라데이션
  const topPlayer = displayScores.length > 0
    ? displayScores.reduce((best, s) => {
        if (!best || (s.totalScore && (!best.totalScore || s.totalScore < best.totalScore))) return s;
        return best;
      }, null)
    : null;
  const topMem = topPlayer ? memberMap[topPlayer.userId] : null;
  const topColor = topMem?.color || (displayScores.some(s => s.totalScore) ? '#2d6a4f' : '#6c757d');

  // hex → rgba 변환
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  };
  // 어두운 버전 (배경용)
  const darken = (hex, amt) => {
    let r = parseInt(hex.slice(1, 3), 16);
    let g = parseInt(hex.slice(3, 5), 16);
    let b = parseInt(hex.slice(5, 7), 16);
    r = Math.max(0, Math.floor(r * (1 - amt)));
    g = Math.max(0, Math.floor(g * (1 - amt)));
    b = Math.max(0, Math.floor(b * (1 - amt)));
    return `#${r.toString(16).padStart(2,'0')}${g.toString(16).padStart(2,'0')}${b.toString(16).padStart(2,'0')}`;
  };

  const headerBg = `linear-gradient(135deg, ${darken(topColor, 0.45)} 0%, ${darken(topColor, 0.2)} 50%, ${topColor} 100%)`;

  const handleDownload = async () => {
    if (!cardRef.current) return;
    try {
      const raw = await html2canvas(cardRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        logging: false,
      });
      // 라운드 모서리 적용
      const radius = 28; // 14px * scale2
      const canvas = document.createElement('canvas');
      canvas.width = raw.width;
      canvas.height = raw.height;
      const ctx = canvas.getContext('2d');
      ctx.beginPath();
      ctx.moveTo(radius, 0);
      ctx.lineTo(raw.width - radius, 0);
      ctx.quadraticCurveTo(raw.width, 0, raw.width, radius);
      ctx.lineTo(raw.width, raw.height - radius);
      ctx.quadraticCurveTo(raw.width, raw.height, raw.width - radius, raw.height);
      ctx.lineTo(radius, raw.height);
      ctx.quadraticCurveTo(0, raw.height, 0, raw.height - radius);
      ctx.lineTo(0, radius);
      ctx.quadraticCurveTo(0, 0, radius, 0);
      ctx.closePath();
      ctx.clip();
      ctx.drawImage(raw, 0, 0);
      const link = document.createElement('a');
      link.download = `${reservation.reservedDate}_${reservation.golfCourseName || ''}_스코어카드.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('이미지 저장 실패:', err);
    }
  };

  return (
    <div className="modal modal-blur show d-block" style={{ background: 'rgba(0,0,0,0.55)' }}
      onMouseDown={(e) => { e.currentTarget._mouseDownTarget = e.target; }}
      onMouseUp={(e) => { if (e.target === e.currentTarget && e.currentTarget._mouseDownTarget === e.currentTarget) onClose(); }}>
      <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable" style={{ maxWidth: 960 }}>
        <div className="modal-content" style={{ borderRadius: 14, overflow: 'hidden', border: 'none' }}>

          {/* 캡처 영역 시작 */}
          <div ref={cardRef} style={{ background: 'var(--tblr-bg-surface, #fff)' }}>

            {/* ── 헤더 (1등 컬러 그라데이션) ── */}
            <div style={{
              background: headerBg,
              color: '#fff', padding: '20px 24px 16px',
            }}>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: 2, opacity: 0.7, textTransform: 'uppercase', marginBottom: 4 }}>
                    SCORE CARD
                  </div>
                  {editing ? (
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                      <div ref={courseDropRef} style={{ position: 'relative' }}>
                        <input type="text" value={courseInput} autoComplete="off"
                          placeholder="골프장 검색"
                          onChange={e => { setCourseInput(e.target.value); setShowCourseList(true); if (!e.target.value) { setLocalCourseId(''); setLocalCourseName(''); setSectionList([]); setLocalSections([]); } }}
                          onFocus={() => setShowCourseList(true)}
                          style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 4, color: '#fff', fontSize: 14, fontWeight: 700, padding: '2px 8px', width: 180 }} />
                        {showCourseList && filteredCourses.length > 0 && (
                          <ul style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1050, maxHeight: 200, overflowY: 'auto', margin: 0, padding: 0, listStyle: 'none', background: '#fff', border: '1px solid #dee2e6', borderRadius: '0 0 4px 4px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                            {filteredCourses.map(c => (
                              <li key={c.id}
                                style={{ padding: '6px 12px', cursor: 'pointer', fontSize: 13, color: '#333', background: String(localCourseId) === String(c.id) ? '#e8f4fd' : undefined }}
                                onMouseDown={() => selectCourse(c)}
                                onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                                onMouseLeave={e => e.currentTarget.style.background = String(localCourseId) === String(c.id) ? '#e8f4fd' : ''}>
                                {c.name}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      {sectionList.length > 0 && (
                        <div className="d-flex align-items-center gap-2" style={{ fontSize: 12 }}>
                          <span style={{ opacity: 0.7, fontWeight: 600 }}>전반</span>
                          <select value={localSections[0]?.sectionId || ''} onChange={e => {
                            const sec = sectionList.find(s => String(s.id) === e.target.value);
                            setLocalSections(prev => {
                              const second = prev[1] || null;
                              if (sec) {
                                const first = { sectionId: sec.id, playOrder: 1, sectionName: sec.name, holesCount: sec.holesCount, parData: sec.parData };
                                return second ? [first, second] : [first];
                              }
                              return second ? [second] : [];
                            });
                          }}
                            style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 4, color: '#fff', fontSize: 12, padding: '1px 4px' }}>
                            <option value="" style={{ color: '#333' }}>선택</option>
                            {sectionList.filter(s => s.useYn !== 'N').map(s => <option key={s.id} value={s.id} style={{ color: '#333' }}>{s.name}</option>)}
                          </select>
                          <span style={{ opacity: 0.7, fontWeight: 600 }}>후반</span>
                          <select value={localSections[1]?.sectionId || ''} onChange={e => {
                            const sec = sectionList.find(s => String(s.id) === e.target.value);
                            setLocalSections(prev => {
                              const first = prev[0] || null;
                              if (sec) {
                                const second = { sectionId: sec.id, playOrder: 2, sectionName: sec.name, holesCount: sec.holesCount, parData: sec.parData };
                                return first ? [first, second] : [second];
                              }
                              return first ? [first] : [];
                            });
                          }}
                            style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 4, color: '#fff', fontSize: 12, padding: '1px 4px' }}>
                            <option value="" style={{ color: '#333' }}>선택</option>
                            {sectionList.filter(s => s.useYn !== 'N').map(s => <option key={s.id} value={s.id} style={{ color: '#333' }}>{s.name}</option>)}
                          </select>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: 1 }}>
                      {localCourseName}
                      {localSections.length > 0 && (
                        <span style={{ fontSize: 13, fontWeight: 500, opacity: 0.7, marginLeft: 8 }}>
                          ({localSections.map(s => s.sectionName).join(' > ')})
                        </span>
                      )}
                    </div>
                  )}
                  {editing ? (
                    <div className="d-flex align-items-center flex-wrap gap-2 mt-1" style={{ fontSize: 12 }}>
                      <input type="date" value={localDate} onChange={e => setLocalDate(e.target.value)}
                        style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 4, color: '#fff', fontSize: 12, padding: '1px 4px' }} />
                      <input type="time" value={localTime} onChange={e => setLocalTime(e.target.value)}
                        style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 4, color: '#fff', fontSize: 12, padding: '1px 4px', width: 90 }} />
                      <select value={localWeather} onChange={e => setLocalWeather(e.target.value)}
                        style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 4, color: '#fff', fontSize: 12, padding: '1px 4px' }}>
                        <option value="" style={{ color: '#333' }}>날씨</option>
                        <option value="sunny" style={{ color: '#333' }}>맑음</option>
                        <option value="cloudy" style={{ color: '#333' }}>흐림</option>
                        <option value="rain" style={{ color: '#333' }}>비</option>
                        <option value="snow" style={{ color: '#333' }}>눈</option>
                        <option value="wind" style={{ color: '#333' }}>바람</option>
                        <option value="storm" style={{ color: '#333' }}>폭풍</option>
                      </select>
                      <input type="text" value={localTemp} onChange={e => setLocalTemp(e.target.value)} placeholder="기온"
                        style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 4, color: '#fff', fontSize: 12, padding: '1px 4px', width: 50, textAlign: 'center' }} />
                      <select value={localHoles} onChange={e => setLocalHoles(e.target.value)}
                        style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 4, color: '#fff', fontSize: 12, padding: '1px 4px' }}>
                        <option value="" style={{ color: '#333' }}>홀</option>
                        <option value="9" style={{ color: '#333' }}>9홀</option>
                        <option value="18" style={{ color: '#333' }}>18홀</option>
                        <option value="27" style={{ color: '#333' }}>27홀</option>
                      </select>
                      <span onClick={toggleGameType}
                        style={{ background: localGameType === '메이저' ? 'rgba(168,85,247,0.3)' : 'rgba(255,255,255,0.2)', padding: '1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                        {localGameType}
                      </span>
                      <span onClick={toggleUseYn}
                        style={{ background: localUseYn === 'N' ? 'rgba(239,68,68,0.3)' : 'rgba(34,197,94,0.3)', padding: '1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                        {localUseYn === 'N' ? '무효' : '유효'}
                      </span>
                      <select value={localStatus} onChange={e => setLocalStatus(e.target.value)}
                        style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 4, color: '#fff', fontSize: 12, padding: '1px 4px' }}>
                        <option value="PENDING" style={{ color: '#333' }}>대기</option>
                        <option value="COMPLETED" style={{ color: '#333' }}>완료</option>
                        <option value="CANCELLED" style={{ color: '#333' }}>취소</option>
                      </select>
                      <input type="text" value={localGreenFee} onChange={e => setLocalGreenFee(e.target.value)} placeholder="그린피"
                        style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 4, color: '#fff', fontSize: 12, padding: '1px 4px', width: 70, textAlign: 'center' }} />
                      <span style={{ fontSize: 11, opacity: 0.7 }}>만원</span>
                      <input type="text" value={localMemo} onChange={e => setLocalMemo(e.target.value)} placeholder="메모"
                        style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 4, color: '#fff', fontSize: 12, padding: '1px 4px', width: 120 }} />
                    </div>
                  ) : (
                    <div className="d-flex align-items-center flex-wrap gap-2 mt-1" style={{ fontSize: 13, opacity: 0.85 }}>
                      <span>{localDate}</span>
                      {localTime && <span>TEE OFF {localTime}</span>}
                      {localWeather && <WeatherBadge code={localWeather} temperature={localTemp} />}
                      {localSections.length > 0 && (() => {
                        const h0 = localSections[0]?.holesCount || 9;
                        if (localSections.length === 1) return <span style={{ background: 'rgba(56,189,248,0.3)', padding: '1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{h0}홀</span>;
                        const h1 = localSections[1]?.holesCount || 9;
                        if (localSections[0]?.sectionId === localSections[1]?.sectionId)
                          return <span style={{ background: 'rgba(168,85,247,0.3)', padding: '1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{h0}x2</span>;
                        return <span style={{ background: 'rgba(34,197,94,0.3)', padding: '1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{h0 + h1}홀</span>;
                      })()}
                      <span style={{ background: localGameType === '메이저' ? 'rgba(168,85,247,0.3)' : 'rgba(255,255,255,0.2)', padding: '1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
                        {localGameType}
                      </span>
                      <span style={{ background: localUseYn === 'N' ? 'rgba(239,68,68,0.3)' : 'rgba(34,197,94,0.3)', padding: '1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
                        {localUseYn === 'N' ? '무효' : '유효'}
                      </span>
                      <span style={{
                        background: localStatus === 'COMPLETED' ? 'rgba(59,130,246,0.3)' : localStatus === 'CANCELLED' ? 'rgba(239,68,68,0.3)' : 'rgba(255,255,255,0.2)',
                        padding: '1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                      }}>
                        {STATUS_MAP[localStatus] || localStatus}
                      </span>
                      {localGreenFee && (
                        <span>{localGreenFee}</span>
                      )}
                      {localMemo && (
                        <span style={{ opacity: 0.75 }}>{localMemo}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* 프로필 이미지 + 스코어 요약 */}
              {displayScores.length > 0 && (
                <div className="d-flex gap-2 mt-3" style={{ flexWrap: 'nowrap', justifyContent: 'flex-end', overflow: 'hidden' }}>
                  {displayScores.map(s => {
                    const mem = memberMap[s.userId];
                    return (
                      <div key={s.playerSlot} className="d-flex align-items-center gap-1"
                        style={{ background: 'rgba(255,255,255,0.12)', borderRadius: 10, padding: '5px 8px', minWidth: 0, flex: '0 1 auto' }}>
                        {mem?.profileImage ? (
                          <img src={mem.profileImage} alt={s.playerName} style={{
                            width: 28, height: 28, borderRadius: '50%', objectFit: 'cover',
                            border: `2px solid ${mem.color || '#fff'}`, flexShrink: 0,
                          }} />
                        ) : (
                          <div style={{
                            width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                            background: mem?.bgcolor || mem?.color || 'rgba(255,255,255,0.3)',
                            color: mem?.bgcolor ? (mem?.color || '#fff') : '#fff',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 12, fontWeight: 700,
                            border: `2px solid ${mem?.color || '#fff'}`,
                          }}>
                            {(mem?.nickname || s.playerName || `P${s.playerSlot}`)[0]?.toUpperCase() || '?'}
                          </div>
                        )}
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 11, fontWeight: 600, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {mem?.nickname || s.playerName || `P${s.playerSlot}`}
                          </div>
                          <div style={{ fontSize: 16, fontWeight: 800, lineHeight: 1, whiteSpace: 'nowrap' }}>
                            {s.totalScore ?? '-'}
                            {s.overPar != null && s.overPar !== 0 && (
                              <span style={{ fontSize: 10, fontWeight: 600, opacity: 0.8, marginLeft: 2 }}>
                                ({s.overPar > 0 ? '+' : ''}{s.overPar})
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ── 본문: 코스별 테이블 ── */}
            <div style={{ maxHeight: '55vh', overflowY: 'auto' }}>
              {loading ? (
                <div className="text-center py-5 text-muted">
                  <span className="spinner-border spinner-border-sm me-2" />불러오는 중...
                </div>
              ) : displayScores.length === 0 ? (
                <div className="text-center py-5 text-muted" style={{ fontSize: 14 }}>
                  <IconTrophy size={36} className="mb-2" style={{ opacity: 0.3 }} />
                  <div>스코어 데이터가 없습니다.</div>
                </div>
              ) : (
                sections.map((sec, secIdx) => {
                  const pars = parsePar(sec.parData);
                  const holes = Array.from({ length: sec.holesCount || 9 }, (_, i) => i + 1);
                  const parSum = pars.reduce((a, b) => a + b, 0);
                  const isLast = secIdx === sections.length - 1;

                  return (
                    <div key={secIdx}>
                      <div className="table-responsive">
                        <table className="table table-sm table-bordered mb-0 text-center scorecard-table"
                          style={{ fontSize: 13, minWidth: 600 }}>
                          <thead>
                            {/* 코스명 헤더 */}
                            <tr style={{ background: darken(topColor, 0.2), color: '#fff' }}>
                              <th style={{ width: 90, textAlign: 'center', fontWeight: 700 }}>
                                {sec.sectionName}
                              </th>
                              {holes.map(h => (
                                <th key={h} style={{ width: 42, fontWeight: 700 }}>{h} H</th>
                              ))}
                              <th style={{ width: 55, fontWeight: 700 }}>
                                {sections.length > 1 ? (secIdx === 0 ? '전반' : '후반') : 'Score'}
                              </th>
                              {sections.length > 1 && (
                                <th style={{ width: 60, fontWeight: 800, background: isLast ? darken(topColor, 0.45) : darken(topColor, 0.2), color: '#fff' }}>
                                  {isLast ? 'Score' : ''}
                                </th>
                              )}
                            </tr>
                            {/* PAR 행 */}
                            <tr className="scorecard-par-row" style={{ background: hexToRgba(topColor, 0.15) }}>
                              <td style={{ textAlign: 'center', fontWeight: 700, color: darken(topColor, 0.3) }}>PAR</td>
                              {holes.map((h, i) => (
                                <td key={h} style={{ fontWeight: 600, color: darken(topColor, 0.3) }}>{pars[i] ?? '-'}</td>
                              ))}
                              <td style={{ fontWeight: 700, color: darken(topColor, 0.3) }}>{parSum}</td>
                              {sections.length > 1 && (
                                <td style={{ fontWeight: 700, color: darken(topColor, 0.3), background: hexToRgba(topColor, isLast ? 0.25 : 0.15) }}>
                                  {isLast ? totalCoursePar : ''}
                                </td>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {displayScores.map((s) => {
                              const slotIdx = currentScores.indexOf(s);
                              const mem = memberMap[s.userId];
                              const hd = parseHoles(s.holeScoresJson, `no${secIdx + 1}`);
                              const pd = parseHoles(s.holeScoresJson, `putting${secIdx + 1}`);
                              const vals = holes.map(h => hd[String(h)]);
                              const putts = holes.map(h => pd[String(h)]);
                              const secSum = vals.reduce((acc, v) => acc + (v !== '' && v != null ? Number(v) : 0), 0);
                              const secPuttSum = putts.reduce((acc, v) => acc + (v !== '' && v != null ? Number(v) : 0), 0);
                              const secStrokeTotal = parSum + secSum;

                              let grandTotal = null;
                              let grandPutting = 0;
                              if (isLast && sections.length > 1) {
                                grandTotal = 0;
                                sections.forEach((ss, si) => {
                                  const hh = parseHoles(s.holeScoresJson, `no${si + 1}`);
                                  const pp = parseHoles(s.holeScoresJson, `putting${si + 1}`);
                                  const hs = Array.from({ length: ss.holesCount || 9 }, (_, i) => i + 1);
                                  hs.forEach(h => {
                                    grandTotal += Number(hh[String(h)] || 0);
                                    grandPutting += Number(pp[String(h)] || 0);
                                  });
                                });
                              }
                              const grandStrokeTotal = grandTotal != null ? totalCoursePar + grandTotal : null;

                              return (
                                <tr key={s.playerSlot} style={{ borderBottom: '1px solid var(--tblr-border-color, #dee2e6)' }}>
                                  <td style={{ textAlign: 'center', fontWeight: 600, verticalAlign: 'middle' }}>
                                    <div className="d-flex align-items-center justify-content-center gap-1">
                                      {mem?.profileImage ? (
                                        <img src={mem.profileImage} alt="" style={{
                                          width: 20, height: 20, borderRadius: '50%', objectFit: 'cover',
                                          border: `1.5px solid ${mem.color || '#ccc'}`,
                                        }} />
                                      ) : (
                                        <div style={{
                                          width: 20, height: 20, borderRadius: '50%', fontSize: 10, fontWeight: 700,
                                          background: mem?.bgcolor || mem?.color || '#dee2e6',
                                          color: mem?.bgcolor ? (mem?.color || '#fff') : '#fff',
                                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                                          border: `1.5px solid ${mem?.color || '#ccc'}`,
                                        }}>
                                          {(mem?.nickname || s.playerName || `P${s.playerSlot}`)[0]?.toUpperCase() || '?'}
                                        </div>
                                      )}
                                      <span style={{ color: mem?.color || undefined, fontSize: 12 }}>
                                        {mem?.nickname || s.playerName || `P${s.playerSlot}`}
                                      </span>
                                      {editing && secIdx === 0 && (
                                        <IconX size={12} style={{ cursor: 'pointer', color: '#d63939', opacity: 0.5, flexShrink: 0 }}
                                          onClick={() => {
                                            if (!window.confirm(`${mem?.nickname || s.playerName || 'P' + s.playerSlot}을(를) 삭제하시겠습니까?`)) return;
                                            setEditScores(prev => prev.filter((_, idx) => idx !== slotIdx));
                                          }} title="삭제" />
                                      )}
                                    </div>
                                  </td>
                                  {vals.map((v, i) => {
                                    const p = putts[i];
                                    const hasPutt = p !== '' && p != null && Number(p) > 0;
                                    const tabBase = (secIdx * 100) + (slotIdx * 20);
                                    return (
                                      <td key={i} style={{ padding: '2px 2px', verticalAlign: 'middle' }}>
                                        {editing ? (
                                          <div>
                                            <input type="number" value={v ?? ''}
                                              tabIndex={tabBase + i + 1}
                                              className={`score-edit-input tabidx${tabBase + i + 1}`}
                                              onChange={e => updateHoleScore(slotIdx, secIdx, holes[i], e.target.value)}
                                              onKeyDown={e => {
                                                const key = e.key;
                                                if (['0','1','2','3','4','5','6','7','8','9','-'].includes(key)) {
                                                  e.preventDefault();
                                                  let newVal;
                                                  if (key === '-') {
                                                    const cur = e.target.value;
                                                    if (cur && cur.startsWith('-')) return;
                                                    newVal = '-' + (cur || '');
                                                    updateHoleScore(slotIdx, secIdx, holes[i], newVal);
                                                    return;
                                                  }
                                                  newVal = key === '9' ? '-1' : key;
                                                  updateHoleScore(slotIdx, secIdx, holes[i], newVal);
                                                  const nextTab = (i === holes.length - 1)
                                                    ? tabBase + holes.length + 1
                                                    : tabBase + i + 2;
                                                  const nextEl = document.querySelector(`.tabidx${nextTab}`);
                                                  if (nextEl) nextEl.focus();
                                                }
                                              }}
                                              onFocus={e => e.target.select()}
                                              style={{
                                                width: 34, textAlign: 'center', border: '1px solid #dee2e6',
                                                borderRadius: 3, fontSize: 12, padding: '2px 0',
                                              }} />
                                            <input type="number" value={p ?? ''}
                                              tabIndex={tabBase + holes.length + i + 1}
                                              className={`score-edit-input tabidx${tabBase + holes.length + i + 1}`}
                                              onChange={e => updatePutting(slotIdx, secIdx, holes[i], e.target.value)}
                                              onKeyDown={e => {
                                                const key = e.key;
                                                if (['0','1','2','3','4','5','6','7','8','9'].includes(key)) {
                                                  e.preventDefault();
                                                  updatePutting(slotIdx, secIdx, holes[i], key);
                                                  const nextTab = tabBase + holes.length + i + 2;
                                                  const nextEl = document.querySelector(`.tabidx${nextTab}`);
                                                  if (nextEl) nextEl.focus();
                                                }
                                              }}
                                              onFocus={e => e.target.select()}
                                              placeholder="P"
                                              style={{
                                                width: 34, textAlign: 'center', border: '1px solid #e8e8e8',
                                                borderRadius: 3, fontSize: 10, padding: '1px 0', marginTop: 1,
                                                color: '#888',
                                              }} />
                                          </div>
                                        ) : (
                                          <>
                                            <ScoreCell value={v} par={pars[i] || 0} />
                                            {hasPutt && (
                                              <div style={{ fontSize: 9, color: '#888', lineHeight: 1, marginTop: 1 }}>{p}</div>
                                            )}
                                          </>
                                        )}
                                      </td>
                                    );
                                  })}
                                  <td style={{ fontWeight: 700, verticalAlign: 'middle' }}>
                                    {editing && sections.length === 1 ? (
                                      <input
                                        type="number"
                                        value={manualScores[s.playerSlot] ?? secStrokeTotal ?? ''}
                                        onChange={e => setManualScores(prev => ({ ...prev, [s.playerSlot]: e.target.value }))}
                                        style={{ width: 50, textAlign: 'center', background: 'rgba(255,255,255,0.9)', border: '1px solid #bbb', borderRadius: 3, fontSize: 13, fontWeight: 700, padding: '2px 2px', color: '#333' }}
                                      />
                                    ) : (
                                      <>
                                        <div>{(sections.length === 1 ? (s.totalScore ?? secStrokeTotal) : secStrokeTotal) || '-'}</div>
                                        {(() => { const ov = sections.length === 1 ? (secSum !== 0 ? secSum : s.overPar) : secSum; return ov != null && ov !== 0 && <div style={{ fontSize: 10, color: ov > 0 ? '#e03131' : '#2fb344' }}>({ov > 0 ? '+' : ''}{ov})</div>; })()}
                                        {secPuttSum > 0 && <div style={{ fontSize: 9, color: '#888' }}>P{secPuttSum}</div>}
                                      </>
                                    )}
                                  </td>
                                  {sections.length > 1 && (
                                    <td className="scorecard-total-cell" style={{ fontWeight: 800, fontSize: 14, background: '#f8f9fa', verticalAlign: 'middle' }}>
                                      {isLast ? (
                                        editing ? (
                                          <input
                                            type="number"
                                            value={manualScores[s.playerSlot] ?? grandStrokeTotal ?? ''}
                                            onChange={e => setManualScores(prev => ({ ...prev, [s.playerSlot]: e.target.value }))}
                                            style={{ width: 50, textAlign: 'center', background: 'rgba(0,0,0,0.07)', border: '1px solid #bbb', borderRadius: 3, fontSize: 13, fontWeight: 700, padding: '2px 2px', color: '#333' }}
                                          />
                                        ) : (
                                          <>
                                            <div>{(s.totalScore ?? grandStrokeTotal) || '-'}</div>
                                            {(() => { const ov = grandTotal !== 0 ? grandTotal : s.overPar; return ov != null && ov !== 0 && <div style={{ fontSize: 10, fontWeight: 600, color: ov > 0 ? '#e03131' : '#2fb344' }}>({ov > 0 ? '+' : ''}{ov})</div>; })()}
                                            {grandPutting > 0 && <div style={{ fontSize: 9, color: '#888' }}>P{grandPutting}</div>}
                                          </>
                                        )
                                      ) : ''}
                                    </td>
                                  )}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
          {/* 캡처 영역 끝 */}

          {/* 사진 (스코어카드 이미지 저장 영역 밖) */}
          {reservation.id && (photos.length > 0 || editing) && (
            <div className="px-3 py-2 border-top">
              <label className="form-label fw-semibold mb-2">사진 {photos.length > 0 && `(${photos.length}/6)`}</label>
              <div className="d-flex flex-wrap gap-2">
                {photos.map(p => (
                  <div key={p.id} className="position-relative" style={{ width: 72, height: 72 }}>
                    <img src={`${api.defaults.baseURL}${p.thumbUrl}`} alt=""
                      onClick={() => setLightboxUrl(`${api.defaults.baseURL}${p.url}`)}
                      className="border rounded"
                      style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }} />
                    {editing && (
                      <button type="button"
                        className="btn btn-sm btn-danger position-absolute p-0 d-flex align-items-center justify-content-center"
                        style={{ width: 20, height: 20, top: -6, right: -6, borderRadius: '50%' }}
                        onClick={() => handleDeletePhoto(p.id)}>
                        <IconX size={12} />
                      </button>
                    )}
                  </div>
                ))}
                {editing && photos.length < 6 && (
                  <label className="d-flex align-items-center justify-content-center border rounded text-muted mb-0"
                    style={{
                      width: 72, height: 72,
                      cursor: photoUploading ? 'default' : 'pointer',
                      opacity: photoUploading ? 0.5 : 1,
                    }}>
                    {photoUploading
                      ? <span className="spinner-border spinner-border-sm" />
                      : <IconPlus size={22} />}
                    <input type="file" accept="image/*" multiple hidden
                      disabled={photoUploading} onChange={handlePhotoSelect} />
                  </label>
                )}
              </div>
            </div>
          )}

          {/* 사진 라이트박스 */}
          {lightboxUrl && (
            <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
              style={{ background: 'rgba(0,0,0,0.85)', zIndex: 2000, cursor: 'zoom-out' }}
              onClick={() => setLightboxUrl(null)}>
              <img src={lightboxUrl} alt="" style={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain' }} />
            </div>
          )}

          {/* 편집 모드: 인원추가 */}
          {editing && (
            <div className="px-3 py-2 border-top" style={{ background: 'var(--tblr-bg-surface-secondary, #f8f9fa)' }}>
              <div className="d-flex align-items-center gap-2">
                <select className="form-select form-select-sm" style={{ width: 'auto', minWidth: 140 }}
                  value={addMemberId} onChange={e => setAddMemberId(e.target.value)}>
                  <option value="">멤버 선택</option>
                  {members.filter(m => !editScores.some(s => s.userId && Number(s.userId) === m.id && s.joinYn !== 'N')).map(m => (
                    <option key={m.id} value={m.id}>{m.nickname || m.name}</option>
                  ))}
                </select>
                <button className="btn btn-sm btn-primary d-flex align-items-center gap-1"
                  disabled={!addMemberId}
                  onClick={() => {
                    const mem = members.find(m => m.id === Number(addMemberId));
                    if (!mem) return;
                    const maxSlot = editScores.reduce((mx, s) => Math.max(mx, s.playerSlot || 0), 0);
                    setEditScores(prev => [...prev, {
                      playerSlot: maxSlot + 1,
                      playerName: mem.nickname || mem.name,
                      userId: mem.id,
                      joinYn: 'Y',
                      totalScore: null, overPar: null, totalPutting: null,
                      holeScoresJson: null, memo: '',
                    }]);
                    setAddMemberId('');
                  }}>
                  <IconPlus size={14} /> 인원추가
                </button>
              </div>
            </div>
          )}

          {/* 푸터 */}
          <div className="modal-footer py-2 d-flex justify-content-between">
            <div className="d-flex gap-2">
              {editing && onDelete && (
                <button className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                  onClick={() => { if (window.confirm('예약을 삭제하시겠습니까?')) onDelete(); }}
                  disabled={saving}>
                  <IconTrash size={14} /> 삭제
                </button>
              )}
              <button className="btn btn-sm d-flex align-items-center gap-1"
                style={{ border: `1px solid ${topColor}`, color: topColor }}
                onClick={handleDownload} disabled={loading || displayScores.length === 0}>
                <IconTrophy size={14} /> 이미지 저장
              </button>
            </div>
            <div className="d-flex gap-2">
              {editing ? (
                <>
                  <button className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                    onClick={() => {
                      if (!window.confirm('모든 점수와 퍼팅을 초기화하시겠습니까?')) return;
                      setEditScores(prev => prev.map(s => ({
                        ...s,
                        holeScoresJson: null,
                        totalScore: null,
                        overPar: null,
                        totalPutting: null,
                      })));
                    }} disabled={saving}>
                    <IconTrash size={14} /> 리셋
                  </button>
                  <button className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                    onClick={cancelEdit} disabled={saving}>
                    <IconX size={14} /> 취소
                  </button>
                  <button className="btn btn-sm btn-warning d-flex align-items-center gap-1"
                    onClick={handleSaveEdit} disabled={saving}>
                    {saving ? <span className="spinner-border spinner-border-sm" /> : <IconDeviceFloppy size={14} />}
                    저장
                  </button>
                </>
              ) : (
                <>
                  <button className="btn btn-sm btn-outline-warning d-flex align-items-center gap-1"
                    onClick={() => scratchData ? setScratchResultOpen(true) : setScratchSettingsOpen(true)}>
                    <IconTrophy size={14} /> 스크라치
                  </button>
                  <button className="btn btn-sm btn-outline-warning d-flex align-items-center gap-1"
                    onClick={startEdit}>
                    <IconEdit size={14} /> 편집
                  </button>
                </>
              )}
              <button className="btn btn-sm" onClick={onClose}>닫기</button>
            </div>
          </div>

          {/* 스크라치 결과 */}
          {scratchResultOpen && scratchData && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              onMouseDown={onBackdropMouseDown} onClick={(e) => onBackdropClick(e, () => setScratchResultOpen(false))}>
              <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 600, maxHeight: '85vh', overflow: 'auto' }}>
                <div ref={scratchRef}>
                  <ScratchResultTable scratchData={scratchData} members={members} scores={scores} />
                </div>
                <div className="d-flex justify-content-between p-2 border-top">
                  <button className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                    onClick={async () => {
                      if (!scratchRef.current) return;
                      try {
                        const raw = await html2canvas(scratchRef.current, { backgroundColor: '#ffffff', scale: 2, useCORS: true, logging: false });
                        const radius = 24;
                        const canvas = document.createElement('canvas');
                        canvas.width = raw.width; canvas.height = raw.height;
                        const ctx = canvas.getContext('2d');
                        ctx.beginPath();
                        ctx.moveTo(radius, 0); ctx.lineTo(raw.width - radius, 0);
                        ctx.quadraticCurveTo(raw.width, 0, raw.width, radius);
                        ctx.lineTo(raw.width, raw.height - radius);
                        ctx.quadraticCurveTo(raw.width, raw.height, raw.width - radius, raw.height);
                        ctx.lineTo(radius, raw.height);
                        ctx.quadraticCurveTo(0, raw.height, 0, raw.height - radius);
                        ctx.lineTo(0, radius);
                        ctx.quadraticCurveTo(0, 0, radius, 0);
                        ctx.closePath(); ctx.clip();
                        ctx.drawImage(raw, 0, 0);
                        const link = document.createElement('a');
                        link.download = `${reservation.reservedDate}_${reservation.golfCourseName || ''}_스크라치.png`;
                        link.href = canvas.toDataURL('image/png');
                        link.click();
                      } catch (err) { console.error('스크라치 이미지 저장 실패:', err); }
                    }}>
                    <IconTrophy size={14} /> 이미지 저장
                  </button>
                  <div className="d-flex gap-2">
                    <button className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                      onClick={() => { setScratchResultOpen(false); setScratchSettingsOpen(true); }}>
                      <IconEdit size={14} /> 설정
                    </button>
                    <button className="btn btn-sm btn-ghost-secondary" onClick={() => setScratchResultOpen(false)}>
                      <IconX size={14} /> 닫기
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 스크라치 설정 */}
          {scratchSettingsOpen && (
            <ScratchSettingsLayer
              reservation={reservation}
              scores={scores}
              members={members}
              existingConfig={scratchConfig}
              onClose={() => setScratchSettingsOpen(false)}
              onConfirm={(cfg) => {
                setScratchConfig(cfg);
                const data = calculateScratch(cfg, scores, sections);
                setScratchData(data);
                setScratchSettingsOpen(false);
                setScratchResultOpen(true);
                if (reservation.id) {
                  api.put(`/golf/reservations/${reservation.id}`, { bettingConfigJson: JSON.stringify(cfg) }).catch(() => {});
                }
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/** 준비중 페이지 */
function ComingSoon({ title }) {
  return (
    <div className="card">
      <div className="card-body text-center py-5">
        <div className="text-muted mb-3">
          <IconTool size={48} style={{ opacity: 0.3 }} />
        </div>
        <h3 className="mb-1">{title}</h3>
        <p className="text-muted">준비중입니다.</p>
      </div>
    </div>
  );
}
